import React, { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import './modernized_app.css';
import CommanderBriefContent from './CommanderBriefContent';
import {
  addDays,
  differenceInMinutes,
  eachDayOfInterval,
  endOfDay,
  format,
  isWithinInterval,
  parseISO,
  startOfDay,
  subDays,
} from 'date-fns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import PptxGenJS from './vendor/pptxgen.cjs.js';
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const STATUS_COLORS = {
  scheduled: '#8aa6bf',
  launched: '#1d9a6c',
  completed: '#2563eb',
  late: '#facc15',
  delayed: '#d97706',
  backlogOpen: '#b45309',
  cancelled: '#c2410c',
  issues: '#b42318',
  redBalls: '#8b1e3f',
  present: '#2563eb',
  unavailable: '#94a3b8',
};

const STATUS_REPORT_PDF_SECTIONS = [
  { key: 'overview', label: 'Summary cards' },
  { key: 'tail-summary', label: 'Tail Summary' },
  { key: 'open-x', label: 'Open X from 8035' },
  { key: 'overdue-9188', label: '9188-6 Overdue' },
  { key: 'red-9188', label: '9188-6 Red Coming Due' },
  { key: 'watch-9188', label: '9188-6 Watch' },
  { key: 'missing-dates', label: 'Items Missing Due & Start Dates' },
];

const DEFAULT_STATUS_REPORT_PDF_SECTIONS = Object.fromEntries(
  STATUS_REPORT_PDF_SECTIONS.map(section => [section.key, true])
);

const STATUS_PDF_CAPTURE_SCALE = 1.15;
const STATUS_PDF_IMAGE_QUALITY = 0.82;
const COMMANDER_PDF_CAPTURE_SCALE = 1.6;

const FORMAL_DELAY_THRESHOLD_MINUTES = 15;

const QUICK_RANGE_BUILDERS = {
  today: () => {
    const now = new Date();
    return { start: now, end: now, label: 'Today' };
  },
  yesterday: () => {
    const yesterday = subDays(new Date(), 1);
    return { start: yesterday, end: yesterday, label: 'Yesterday' };
  },
  last7: () => {
    const now = new Date();
    return { start: subDays(now, 6), end: now, label: 'Last 7 Days' };
  },
  last30: () => {
    const now = new Date();
    return { start: subDays(now, 29), end: now, label: 'Last 30 Days' };
  },
};

const SMART_CUSTOM_REPORT_PRESETS = [
  {
    key: 'mission-impact',
    label: 'Mission Impact',
    prompt: 'What issues, delays, cancellations, or spare swaps most affected mission execution in this date range?',
  },
  {
    key: 'recurring-drivers',
    label: 'Recurring Drivers',
    prompt: 'What problems keep repeating by tail, shop, issue type, or schedule pattern?',
  },
  {
    key: 'supply-risk',
    label: 'Supply Risk',
    prompt: 'Which supply items appear most likely to delay return to FMC or future sorties?',
  },
  {
    key: 'personnel-coverage',
    label: 'Personnel Coverage',
    prompt: 'Where are personnel coverage gaps, workload imbalances, or launch/recovery assignment risks showing up?',
  },
  {
    key: 'safety-patterns',
    label: 'Safety Patterns',
    prompt: 'What safety, injury, lost tool, or UXO patterns should leaders watch in this period?',
  },
  {
    key: 'planning-priorities',
    label: 'Plans / Priorities',
    prompt: 'What aircraft plan events and priority work items could affect the schedule or commander decisions in this period?',
  },
];

const SMART_CUSTOM_REPORT_DOMAINS = [
  { key: 'operations', label: 'Fly Schedule' },
  { key: 'acft_plans', label: 'ACFT Plans' },
  { key: 'priorities', label: 'Priorities' },
  { key: 'issues', label: 'Issues' },
  { key: 'status', label: 'Status' },
  { key: 'supply', label: 'Supply' },
  { key: 'personnel', label: 'Personnel' },
  { key: 'safety', label: 'Safety' },
  { key: 'change_log', label: 'Change Log' },
  { key: 'weather', label: 'Weather' },
];

function toDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toDateKey(value) {
  const date = toDate(value);
  return date ? format(date, 'yyyy-MM-dd') : null;
}

function buildInputDateValue(value) {
  return format(value, 'yyyy-MM-dd');
}

function parseDateKey(dateKey) {
  if (!dateKey) return null;
  try {
    return parseISO(dateKey);
  } catch (err) {
    return null;
  }
}

function isCompleteDateInput(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value || '');
}

function matchesDayRange(startKey, endKey, range) {
  return startKey === buildInputDateValue(range.start) && endKey === buildInputDateValue(range.end);
}

function getRangeLabel(startKey, endKey) {
  const quickRanges = Object.values(QUICK_RANGE_BUILDERS).map((build) => build());
  const matched = quickRanges.find((range) => matchesDayRange(startKey, endKey, range));
  return matched ? matched.label : 'Custom';
}

function getRangeDescription(rangeLabel, startKey, endKey) {
  if (rangeLabel !== 'Custom') return rangeLabel.toLowerCase();
  if (startKey && endKey) return `${startKey} to ${endKey}`;
  return 'the selected custom range';
}

function getFlightDay(row) {
  return parseDateKey(row.takeoffDate);
}

function getIssueOpenedAt(issue) {
  return toDate(issue.called || issue.createdAt || issue.arrived);
}

function getIssueClosedAt(issue) {
  return toDate(issue.fixed);
}

function getScheduledDateTime(row, scheduleKey) {
  const utcKeyMap = {
    schedMxShow: 'schedMxShowUtc',
    schedCrewShow: 'schedCrewShowUtc',
    schedEngStrt: 'schedEngStrtUtc',
    schedTaxi: 'schedTaxiUtc',
    schedTakeoff: 'schedTakeoffUtc',
    schedLand: 'schedLandUtc',
  };
  const offsetKeyMap = {
    schedMxShow: 'schedMxShowOffsetDays',
    schedCrewShow: 'schedCrewShowOffsetDays',
    schedEngStrt: 'schedEngStrtOffsetDays',
    schedTaxi: 'schedTaxiOffsetDays',
    schedLand: 'schedLandOffsetDays',
  };

  const utcValue = toDate(row[utcKeyMap[scheduleKey]]);
  if (utcValue) return utcValue;

  const flightDay = row.takeoffDate;
  const timeValue = row[scheduleKey];
  if (!flightDay || !timeValue) return null;
  const date = toDate(`${flightDay}T${String(timeValue).trim()}`);
  if (!date) return null;

  const offsetValue = Number((offsetKeyMap[scheduleKey] ? row[offsetKeyMap[scheduleKey]] : 0) || 0);
  if (Number.isInteger(offsetValue) && offsetValue !== 0) {
    date.setDate(date.getDate() + offsetValue);
  }
  return date;
}

function getActualDateTime(row, actualKey) {
  return toDate(row[actualKey]);
}

function formatScheduledMilestoneDisplay(row, scheduleKey) {
  const scheduledAt = getScheduledDateTime(row, scheduleKey);
  if (!scheduledAt) return '--';

  const offsetKeyMap = {
    schedMxShow: 'schedMxShowOffsetDays',
    schedCrewShow: 'schedCrewShowOffsetDays',
    schedEngStrt: 'schedEngStrtOffsetDays',
    schedTaxi: 'schedTaxiOffsetDays',
    schedLand: 'schedLandOffsetDays',
  };
  const offsetValue = Number((offsetKeyMap[scheduleKey] ? row[offsetKeyMap[scheduleKey]] : 0) || 0);

  if (offsetValue === 0) {
    return format(scheduledAt, 'HH:mm');
  }

  return `${format(scheduledAt, 'MM/dd HH:mm')} (${offsetValue > 0 ? `+${offsetValue}` : offsetValue})`;
}

function hasTakenOff(row) {
  return Boolean(row.actTakeoff);
}

function isCompletedSortie(row) {
  return Boolean(row.actLand);
}

function isLaunchedInProgress(row) {
  return hasTakenOff(row) && !isCompletedSortie(row);
}

function positiveDelayMinutes(actualDate, scheduledDate) {
  if (!actualDate || !scheduledDate) return 0;
  return Math.max(0, differenceInMinutes(actualDate, scheduledDate));
}

function minutesToHours(minutes) {
  return minutes / 60;
}

function formatMinutes(value) {
  return value > 0 ? `${Math.round(value)} min` : '0 min';
}

function formatHours(value) {
  return `${value.toFixed(1)} hrs`;
}

function formatPercent(value) {
  return `${Math.round(value)}%`;
}

function formatReportDate(value) {
  const date = toDate(value);
  return date ? format(date, 'MMM d, yyyy') : '--';
}

function formatReportDateTime(value) {
  const date = toDate(value);
  return date ? format(date, 'MMM d, yyyy HH:mm') : '--';
}

function truncateReportText(value, maxLength = 96) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '--';
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}...` : text;
}

function aircraftStatusTone(status) {
  const normalized = String(status || '').trim().toUpperCase();
  if (normalized === 'CANN') return 'cann';
  if (normalized.startsWith('P')) return 'p';
  if (normalized.startsWith('FMC') || normalized === 'GAR') return 'mc';
  if (normalized.startsWith('NMC')) return 'nmc';
  return 'neutral';
}

function AircraftStatusPill({ status, className = '' }) {
  const label = status || '--';
  return (
    <span className={['reports-status-pill', `reports-status-pill--${aircraftStatusTone(label)}`, className].filter(Boolean).join(' ')}>
      {label}
    </span>
  );
}

function filterStatusReportTails(tails, scope) {
  return (tails || []).filter((tailRow) => {
    const hsOs = String(tailRow.aircraft?.hsOs || 'HS').trim().toUpperCase();
    if (scope === 'hs') return hsOs !== 'OS';
    if (scope === 'os') return hsOs === 'OS';
    return true;
  });
}

function summarizeStatusReportTails(tails) {
  return (tails || []).reduce((summary, tailRow) => {
    const counts = tailRow.counts || {};
    const openX = Number(counts.openX || 0);
    summary.tails += 1;
    summary.openX += openX;
    summary.overdue9188 += Number(counts.overdue9188 || 0);
    summary.red9188 += Number(counts.red9188 || 0);
    summary.watch9188 += Number(counts.watch9188 || 0);
    summary.missingDueStart += Number(counts.missingDueStart || 0);
    summary.form781K += Number(counts.form781K || 0);
    if (openX > 0) summary.tailsWithOpenX += 1;
    return summary;
  }, {
    tails: 0,
    tailsWithOpenX: 0,
    openX: 0,
    overdue9188: 0,
    red9188: 0,
    watch9188: 0,
    missingDueStart: 0,
    form781K: 0,
  });
}

function flattenStatusReportTailItems(tails, key) {
  return (tails || []).flatMap((tailRow) => {
    const aircraft = tailRow.aircraft || {};
    return (tailRow[key] || []).map((item) => ({
      ...item,
      aircraftId: aircraft.id || '',
      tail: aircraft.tail || '--',
      mds: aircraft.mds || '--',
      status: aircraft.status || '--',
      tailLocation: aircraft.location || '',
      hsOs: aircraft.hsOs || '',
    }));
  });
}

function compareStatusReportTailGroups(left, right) {
  const leftOverdue = Number(left?.counts?.overdue9188 || left?.overdueCount || 0);
  const rightOverdue = Number(right?.counts?.overdue9188 || right?.overdueCount || 0);
  if (leftOverdue !== rightOverdue) return rightOverdue - leftOverdue;
  const leftItems = Number(left?.items?.length || 0);
  const rightItems = Number(right?.items?.length || 0);
  if (leftItems !== rightItems) return rightItems - leftItems;
  return String(left?.aircraft?.tail || left?.tail || '').localeCompare(
    String(right?.aircraft?.tail || right?.tail || ''),
    undefined,
    { numeric: true }
  );
}

function buildStatusReportTailItemGroups(tails, keys) {
  const itemKeys = Array.isArray(keys) ? keys : [keys];
  return (tails || [])
    .map((tailRow) => {
      const aircraft = tailRow.aircraft || {};
      const items = itemKeys.flatMap((key) => (tailRow[key] || []).map((item) => ({
        ...item,
        aircraftId: aircraft.id || '',
        tail: aircraft.tail || '--',
        mds: aircraft.mds || '--',
        status: aircraft.status || '--',
        tailLocation: aircraft.location || '',
        hsOs: aircraft.hsOs || '',
        groupKey: key,
      })));
      return {
        id: aircraft.id || aircraft.tail || itemKeys.join('-'),
        aircraft,
        counts: tailRow.counts || {},
        overdueCount: Number(tailRow.counts?.overdue9188 ?? tailRow.overdue9188?.length ?? 0),
        items,
      };
    })
    .filter(group => group.items.length > 0)
    .sort(compareStatusReportTailGroups);
}

function flattenStatusReportTailItemGroups(groups) {
  return (groups || []).flatMap(group => group.items);
}

function StatusReportTailGroupHeader({ group, colSpan, label }) {
  const aircraft = group.aircraft || {};
  const itemCount = group.items?.length || 0;
  const overdueCount = Number(group.overdueCount || 0);
  return (
    <tr className="reports-status-tail-group-row">
      <td colSpan={colSpan}>
        <div className="reports-status-tail-group">
          <span className="reports-status-tail-group__identity">
            <strong>{aircraft.tail || '--'}</strong>
            <span>{[aircraft.mds || '--', aircraft.hsOs || '--'].join(' | ')}</span>
          </span>
          <span className="reports-status-tail-group__count">
            {itemCount} {label}{itemCount === 1 ? '' : 's'}{overdueCount ? ` | ${overdueCount} overdue` : ''}
          </span>
        </div>
      </td>
    </tr>
  );
}

function StatusReportScopeToggle({ scope, onChange }) {
  const options = [
    ['combined', 'Combined'],
    ['hs', 'Homestation'],
    ['os', 'Offstation'],
  ];

  return (
    <div className="status-scope-toggle" aria-label="Status report scope">
      {options.map(([value, label]) => (
        <button
          key={value}
          type="button"
          className={`status-scope-toggle__button ${scope === value ? 'is-active' : ''}`}
          onClick={() => onChange(value)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function roundToOne(value) {
  return Math.round(value * 10) / 10;
}

function mapCounts(entries, labelKey = 'label', valueKey = 'value') {
  const counts = new Map();
  entries.forEach((entry) => {
    if (!entry) return;
    counts.set(entry, (counts.get(entry) || 0) + 1);
  });

  return [...counts.entries()]
    .map(([label, value]) => ({ [labelKey]: label, [valueKey]: value }))
    .sort((left, right) => right[valueKey] - left[valueKey]);
}

function categorizeReason(reasonText) {
  const value = (reasonText || '').toLowerCase();
  if (!value) return 'Unspecified';
  if (value.includes('weather')) return 'Weather';
  if (value.includes('part') || value.includes('supply') || value.includes('backorder')) return 'Awaiting Parts';
  if (value.includes('fuel')) return 'Fuel';
  if (value.includes('crew') || value.includes('ops')) return 'Crew / Ops Delay';
  if (value.includes('specialist') || value.includes('personnel') || value.includes('staff')) return 'Specialist Unavailable';
  if (value.includes('age') || value.includes('munition') || value.includes('support')) return 'External Support';
  if (value.includes('repeat')) return 'Repeat Discrepancy';
  if (value.includes('preflight')) return 'Preflight Inspection Incomplete';
  if (value.includes('maintenance') || value.includes('mx') || value.includes('discrep')) return 'Maintenance Issue';
  return 'Other';
}

function issueTouchesFlightWindow(issue, flightDate) {
  const openedAt = getIssueOpenedAt(issue);
  if (!openedAt || !flightDate) return true;
  const windowStart = startOfDay(subDays(flightDate, 7));
  const windowEnd = endOfDay(addDays(flightDate, 1));
  return isWithinInterval(openedAt, { start: windowStart, end: windowEnd });
}

function getDelayDriver(row, linkedIssues) {
  const mxDelay = positiveDelayMinutes(
    getActualDateTime(row, 'actMxShow'),
    getScheduledDateTime(row, 'schedMxShow')
  );
  const crewDelay = positiveDelayMinutes(
    getActualDateTime(row, 'actCrewShow'),
    getScheduledDateTime(row, 'schedCrewShow')
  );
  const issueReasons = linkedIssues.map((issue) => categorizeReason(issue.description));

  if (issueReasons.includes('Awaiting Parts')) return 'Awaiting Parts';
  if (linkedIssues.length > 0) return 'Maintenance Issue';
  if (mxDelay > 0) return 'MX Show Miss';
  if (!(row.launchPersonnel || []).length) return 'MX Show Miss';
  if (crewDelay > 0) return 'Crew / Ops Delay';
  return 'Other / Unknown';
}

function calculateAverage(values) {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function distinctCount(values) {
  return new Set(values.filter(Boolean)).size;
}

function formatListPreview(values, max = 3) {
  const uniqueValues = [...new Set(values.filter(Boolean))];
  if (!uniqueValues.length) return 'None';
  const preview = uniqueValues.slice(0, max).join(', ');
  return uniqueValues.length > max ? `${preview}, +${uniqueValues.length - max} more` : preview;
}

function buildWindowSummary(flights, issues, currentOpenIssues) {
  const launched = flights.filter(isLaunchedInProgress);
  const completed = flights.filter(isCompletedSortie);
  const cancelled = flights.filter((row) => Boolean(row.cancelled));
  const tookOff = flights.filter(hasTakenOff);
  const lateTakeoffMinutes = flights
    .map((row) => positiveDelayMinutes(getActualDateTime(row, 'actTakeoff'), getScheduledDateTime(row, 'schedTakeoff')))
    .filter((minutes) => minutes > 0);
  const delayMinutes = lateTakeoffMinutes.filter((minutes) => minutes >= FORMAL_DELAY_THRESHOLD_MINUTES);
  const late = lateTakeoffMinutes.filter((minutes) => minutes < FORMAL_DELAY_THRESHOLD_MINUTES).length;
  const delayed = delayMinutes.length;

  return {
    scheduled: flights.length,
    launched: launched.length,
    completed: completed.length,
    cancelled: cancelled.length,
    late,
    delayed,
    avgDelayMinutes: calculateAverage(delayMinutes),
    launchSuccessRate: flights.length ? (tookOff.length / flights.length) * 100 : 0,
    onTimeTakeoffRate: tookOff.length ? ((tookOff.length - lateTakeoffMinutes.length) / tookOff.length) * 100 : 0,
    openIssues: currentOpenIssues.length,
    issuesOpened: issues.length,
  };
}

function buildNarrative(selectedSummary, delayDrivers, cancellationCauses, repeatTailRows, riskRows) {
  const topDelay = delayDrivers[0]?.label || 'no major delay driver';
  const topCancel = cancellationCauses[0]?.label || 'no dominant cancellation cause';
  const topTail = repeatTailRows[0]?.tail || 'no concentrated tail issue pattern';
  const topRisk = riskRows[0]?.text || 'no high-risk indicator triggered';

  return `Scheduled ${selectedSummary.scheduled} sorties in the selected window, launched ${selectedSummary.launched}, completed ${selectedSummary.completed}, and cancelled ${selectedSummary.cancelled}. Delays averaged ${Math.round(selectedSummary.avgDelayMinutes || 0)} minutes, with ${topDelay.toLowerCase()} leading the pressure points. Cancellations were driven most by ${topCancel.toLowerCase()}, while ${topTail} has the highest tail issue concentration. Immediate leadership watch item: ${topRisk.toLowerCase()}.`;
}

export default function ReportsTab({ api, user, selectedBase, globalView }) {
  const initialRange = QUICK_RANGE_BUILDERS.last7();
  const initialStartDate = buildInputDateValue(initialRange.start);
  const initialEndDate = buildInputDateValue(initialRange.end);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [draftStartDate, setDraftStartDate] = useState(initialStartDate);
  const [draftEndDate, setDraftEndDate] = useState(initialEndDate);
  const [atoData, setAtoData] = useState([]);
  const [personnelData, setPersonnelData] = useState([]);
  const [issuesData, setIssuesData] = useState([]);
  const [featureFlags, setFeatureFlags] = useState({});
  const [activeDelayDriver, setActiveDelayDriver] = useState('');
  const [activeDelayTail, setActiveDelayTail] = useState('');
  const [activeDelayTrendDay, setActiveDelayTrendDay] = useState('');
  const [activeDelayTrendType, setActiveDelayTrendType] = useState('');
  const [activeCancellationCause, setActiveCancellationCause] = useState('');
  const [activeRepeatTail, setActiveRepeatTail] = useState('');
  const [activeBacklogShop, setActiveBacklogShop] = useState('');
  const [activeReportPage, setActiveReportPage] = useState('main');
  const [commanderBrief, setCommanderBrief] = useState(null);
  const [commanderBriefHistory, setCommanderBriefHistory] = useState([]);
  const [commanderBriefHistoryLoading, setCommanderBriefHistoryLoading] = useState(false);
  const [commanderBriefLoading, setCommanderBriefLoading] = useState(false);
  const [commanderBriefError, setCommanderBriefError] = useState('');
  const [safetyBrief, setSafetyBrief] = useState(null);
  const [safetyBriefHistory, setSafetyBriefHistory] = useState([]);
  const [safetyBriefHistoryLoading, setSafetyBriefHistoryLoading] = useState(false);
  const [safetyBriefLoading, setSafetyBriefLoading] = useState(false);
  const [safetyBriefError, setSafetyBriefError] = useState('');
  const [customReport, setCustomReport] = useState(null);
  const [customReportHistory, setCustomReportHistory] = useState([]);
  const [customReportHistoryLoading, setCustomReportHistoryLoading] = useState(false);
  const [customReportLoading, setCustomReportLoading] = useState(false);
  const [customReportError, setCustomReportError] = useState('');
  const [customReportQuestion, setCustomReportQuestion] = useState(SMART_CUSTOM_REPORT_PRESETS[0].prompt);
  const [customReportPreset, setCustomReportPreset] = useState(SMART_CUSTOM_REPORT_PRESETS[0].key);
  const [customReportDomains, setCustomReportDomains] = useState(SMART_CUSTOM_REPORT_DOMAINS.map(domain => domain.key));
  const [statusReport, setStatusReport] = useState(null);
  const [statusReportLoading, setStatusReportLoading] = useState(false);
  const [statusReportError, setStatusReportError] = useState('');
  const [statusReportScope, setStatusReportScope] = useState('combined');
  const [statusLinked9188Filter, setStatusLinked9188Filter] = useState('');
  const [statusPdfSections, setStatusPdfSections] = useState(DEFAULT_STATUS_REPORT_PDF_SECTIONS);
  const [statusPdfPickerOpen, setStatusPdfPickerOpen] = useState(false);
  const allCustomReportDomainsSelected = customReportDomains.length === SMART_CUSTOM_REPORT_DOMAINS.length;
  const reportRef = useRef(null);
  const statusReportRef = useRef(null);
  const delayPanelRef = useRef(null);
  const spareSwapPanelRef = useRef(null);
  const repeatTailPanelRef = useRef(null);
  const backlogPanelRef = useRef(null);
  const workforcePanelRef = useRef(null);
  const recoverySchedulingEnabled = Boolean(featureFlags.recovery_personnel_scheduling);
  const commanderAiBriefEnabled = Boolean(featureFlags.commander_ai_brief);
  const commanderAiBriefLocked = Boolean(selectedBase && !commanderAiBriefEnabled);
  const smartCustomReportBuilderEnabled = Boolean(featureFlags.smart_custom_report_builder);
  const smartCustomReportBuilderLocked = Boolean(selectedBase && !smartCustomReportBuilderEnabled);

  const hydrateSavedBrief = (record) => record?.brief ? {
    ...record.brief,
    savedBriefId: record.id,
    savedRecord: record,
    generatedAt: record.generatedAt || record.brief.generatedAt,
    mode: record.brief.mode || record.mode,
    provider: record.brief.provider || record.provider,
    model: record.brief.model || record.model,
    aiConfigured: record.brief.aiConfigured ?? record.aiConfigured,
    aiError: record.brief.aiError || record.aiError,
  } : null;

  useEffect(() => {
    async function fetchRawData() {
      try {
        const params = {
          email: user.email,
          location: selectedBase,
          global: user.is_superuser && globalView ? true : undefined
        };
        const [atoRes, personnelRes, issuesRes] = await Promise.all([
          api.get('/get-ato', { params }),
          api.get('/get-personnel', { params }),
          api.get('/get-issues-log', { params })
        ]);

        setAtoData(atoRes.data.data || []);
        setFeatureFlags(atoRes.data.featureFlags || {});
        setPersonnelData(personnelRes.data.data || []);
        setIssuesData(issuesRes.data.data || []);
      } catch (err) {
        console.error('Failed to fetch report data', err);
      }
    }

    fetchRawData();
  }, [api, user, selectedBase, globalView]);

  useEffect(() => {
    async function loadStatusReport() {
      if (!selectedBase || !user?.email) {
        setStatusReport(null);
        setStatusReportError('');
        return;
      }
      setStatusReportLoading(true);
      setStatusReportError('');
      try {
        const res = await api.get('/g081-imports/status-report', {
          params: {
            email: user.email,
            location: selectedBase,
            global: user.is_superuser && globalView ? true : undefined,
          },
        });
        setStatusReport(res.data || null);
      } catch (err) {
        console.error('Failed to load G081 status report', err);
        setStatusReport(null);
        setStatusReportError(err.response?.data?.message || 'Status report could not be loaded.');
      } finally {
        setStatusReportLoading(false);
      }
    }

    loadStatusReport();
  }, [api, user, selectedBase, globalView]);

  useEffect(() => {
    setCommanderBriefError('');
    async function loadSavedCommanderBriefs() {
      if (!selectedBase || !user?.email) {
        setCommanderBrief(null);
        setCommanderBriefHistory([]);
        return;
      }

      setCommanderBriefHistoryLoading(true);
      try {
        const res = await api.get('/commander-briefs', {
          params: {
            email: user.email,
            location: selectedBase,
            global: user.is_superuser && globalView ? true : undefined,
            startDate,
            endDate,
            limit: 12,
          },
        });
        const history = res.data.history || [];
        const latest = res.data.latest;
        setCommanderBriefHistory(history);
        setCommanderBrief(hydrateSavedBrief(latest));
      } catch (err) {
        console.error('Failed to load saved commander briefs', err);
        setCommanderBriefHistory([]);
        setCommanderBrief(null);
      } finally {
        setCommanderBriefHistoryLoading(false);
      }
    }

    loadSavedCommanderBriefs();
  }, [api, user, selectedBase, globalView, startDate, endDate]);

  useEffect(() => {
    setSafetyBriefError('');
    async function loadSavedSafetyBriefs() {
      if (!selectedBase || !user?.email) {
        setSafetyBrief(null);
        setSafetyBriefHistory([]);
        return;
      }

      setSafetyBriefHistoryLoading(true);
      try {
        const res = await api.get('/commander-briefs', {
          params: {
            email: user.email,
            location: selectedBase,
            global: user.is_superuser && globalView ? true : undefined,
            startDate,
            endDate,
            limit: 12,
            reportType: 'commander_safety_intelligence',
          },
        });
        setSafetyBriefHistory(res.data.history || []);
        setSafetyBrief(hydrateSavedBrief(res.data.latest));
      } catch (err) {
        console.error('Failed to load saved safety intelligence reports', err);
        setSafetyBriefHistory([]);
        setSafetyBrief(null);
      } finally {
        setSafetyBriefHistoryLoading(false);
      }
    }

    loadSavedSafetyBriefs();
  }, [api, user, selectedBase, globalView, startDate, endDate]);

  useEffect(() => {
    setCustomReportError('');
    async function loadSavedCustomReports() {
      if (!selectedBase || !user?.email) {
        setCustomReport(null);
        setCustomReportHistory([]);
        return;
      }

      setCustomReportHistoryLoading(true);
      try {
        const res = await api.get('/commander-briefs', {
          params: {
            email: user.email,
            location: selectedBase,
            global: user.is_superuser && globalView ? true : undefined,
            startDate,
            endDate,
            limit: 12,
            reportType: 'smart_custom_report_builder',
          },
        });
        setCustomReportHistory(res.data.history || []);
        setCustomReport(hydrateSavedBrief(res.data.latest));
      } catch (err) {
        console.error('Failed to load saved custom reports', err);
        setCustomReportHistory([]);
        setCustomReport(null);
      } finally {
        setCustomReportHistoryLoading(false);
      }
    }

    loadSavedCustomReports();
  }, [api, user, selectedBase, globalView, startDate, endDate]);

  const rangeStartRaw = startOfDay(parseDateKey(startDate) || new Date());
  const rangeEndRaw = endOfDay(parseDateKey(endDate) || new Date());
  const rangeStart = rangeStartRaw <= rangeEndRaw ? rangeStartRaw : startOfDay(rangeEndRaw);
  const rangeEnd = rangeEndRaw >= rangeStartRaw ? rangeEndRaw : endOfDay(rangeStartRaw);
  const activeRangeLabel = getRangeLabel(startDate, endDate);
  const activeRangeDescription = getRangeDescription(activeRangeLabel, startDate, endDate);
  const canApplyDraftRange = isCompleteDateInput(draftStartDate) && isCompleteDateInput(draftEndDate);
  const hasPendingDraftRange = draftStartDate !== startDate || draftEndDate !== endDate;

  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);
  const yesterdayStart = startOfDay(subDays(today, 1));
  const yesterdayEnd = endOfDay(subDays(today, 1));
  const last7Start = startOfDay(subDays(today, 6));
  const last7End = endOfDay(today);
  const last30Start = startOfDay(subDays(today, 29));
  const tomorrowKey = format(addDays(today, 1), 'yyyy-MM-dd');

  const flightsByDateKey = useMemo(() => (
    atoData.reduce((map, row) => {
      const key = row.takeoffDate;
      if (!key) return map;
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push(row);
      return map;
    }, {})
  ), [atoData]);

  const issuesByOpenedDateKey = useMemo(() => (
    issuesData.reduce((map, issue) => {
      const openedAt = getIssueOpenedAt(issue);
      const key = openedAt ? toDateKey(openedAt) : null;
      if (!key) return map;
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push(issue);
      return map;
    }, {})
  ), [issuesData]);

  const flightsInRange = atoData.filter((row) => {
    const flightDay = getFlightDay(row);
    return flightDay ? isWithinInterval(flightDay, { start: rangeStart, end: rangeEnd }) : false;
  });

  const openIssuesCurrent = issuesData.filter((issue) => !issue.fixed);
  const openRedBallsCurrent = openIssuesCurrent.filter((issue) => issue.redBallFlag);

  const issuesOpenedInRange = issuesData.filter((issue) => {
    const openedAt = getIssueOpenedAt(issue);
    return openedAt ? isWithinInterval(openedAt, { start: rangeStart, end: rangeEnd }) : false;
  });

  const issuesClosedInRange = issuesData.filter((issue) => {
    const closedAt = getIssueClosedAt(issue);
    return closedAt ? isWithinInterval(closedAt, { start: rangeStart, end: rangeEnd }) : false;
  });

  const issuesByTail = issuesData.reduce((map, issue) => {
    const key = issue.tail || 'Unknown';
    if (!map[key]) {
      map[key] = [];
    }
    map[key].push(issue);
    return map;
  }, {});

  const delayedSorties = flightsInRange
    .map((row) => {
      const scheduledTakeoff = getScheduledDateTime(row, 'schedTakeoff');
      const actualTakeoff = getActualDateTime(row, 'actTakeoff');
      const delayMinutes = positiveDelayMinutes(actualTakeoff, scheduledTakeoff);
      const linkedIssues = (issuesByTail[row.tail] || []).filter((issue) =>
        issueTouchesFlightWindow(issue, getFlightDay(row))
      );
      return {
        ...row,
        delayMinutes,
        delayDriver: getDelayDriver(row, linkedIssues),
        linkedIssues,
      };
    })
    .filter((row) => row.delayMinutes >= FORMAL_DELAY_THRESHOLD_MINUTES)
    .sort((left, right) => right.delayMinutes - left.delayMinutes);

  const cancelledSorties = flightsInRange
    .filter((row) => row.cancelled)
    .map((row) => {
      const linkedIssues = (issuesByTail[row.tail] || []).filter((issue) =>
        issueTouchesFlightWindow(issue, getFlightDay(row))
      );
      const fallbackReason = linkedIssues.length > 0 ? 'Maintenance Issue' : 'Unspecified';
      const cancelReasonLabel = String(row.cancelReason || '').trim() || fallbackReason;
      return {
        ...row,
        cancelReasonLabel,
        cancelCategory: categorizeReason(cancelReasonLabel),
        cancellationNotes: row.comments || '',
      };
    });

  const mxShowMisses = flightsInRange
    .map((row) => {
      const delayMinutes = positiveDelayMinutes(
        getActualDateTime(row, 'actMxShow'),
        getScheduledDateTime(row, 'schedMxShow')
      );
      const linkedIssues = (issuesByTail[row.tail] || []).filter((issue) =>
        issueTouchesFlightWindow(issue, getFlightDay(row))
      );
      return {
        ...row,
        delayMinutes,
        linkedIssues,
      };
    })
    .filter((row) => row.delayMinutes > 0)
    .sort((left, right) => right.delayMinutes - left.delayMinutes);

  const launchCoverageGaps = flightsInRange.filter((row) => !(row.launchPersonnel || []).length);
  const personnelByEmpNum = new Map(
    personnelData
      .map((person) => [String(person['Emp Num'] || '').trim(), person])
      .filter(([empNum]) => Boolean(empNum))
  );
  const recoveryFlights = flightsInRange.filter((row) => row.schedLand && !row.cancelled);
  const recoveryCoverageGaps = recoveryFlights.filter((row) => (row.recoveryPersonnel || []).length < 2);
  const recoveryAssignments = recoveryFlights.flatMap((row) => (
    (row.recoveryPersonnel || []).map((empNum) => {
      const cleanEmpNum = String(empNum || '').trim();
      const person = personnelByEmpNum.get(cleanEmpNum);
      return {
        empNum: cleanEmpNum,
        person,
        row,
        shop: person?.['Shop Name'] || 'Unknown',
        name: person
          ? `${person['Last Name'] || ''}, ${person['First Name'] || ''}`.replace(/^,\s*/, '').trim() || cleanEmpNum
          : cleanEmpNum,
      };
    })
  ));
  const recoveryPersonnelLoad = [...recoveryAssignments.reduce((map, assignment) => {
    const key = assignment.empNum || assignment.name;
    if (!key) return map;
    const current = map.get(key) || {
      empNum: assignment.empNum,
      name: assignment.name,
      shop: assignment.shop,
      assignments: 0,
      tails: new Set(),
      completed: 0,
      upcoming: 0,
    };
    current.assignments += 1;
    if (assignment.row.tail) current.tails.add(assignment.row.tail);
    if (assignment.row.actLand) current.completed += 1;
    else current.upcoming += 1;
    map.set(key, current);
    return map;
  }, new Map()).values()]
    .map((row) => ({
      ...row,
      uniqueTails: row.tails.size,
    }))
    .sort((left, right) => right.assignments - left.assignments || left.name.localeCompare(right.name))
    .slice(0, 8);
  const recoveryShopLoad = mapCounts(recoveryAssignments.map((assignment) => assignment.shop), 'shop', 'count').slice(0, 6);
  const recoveryCoverageRate = recoveryFlights.length
    ? ((recoveryFlights.length - recoveryCoverageGaps.length) / recoveryFlights.length) * 100
    : 0;
  const spareSwapRows = flightsInRange
    .filter((row) => row.spareSwappedIn)
    .sort((left, right) => {
      const rightTime = toDate(right.spareSwapAt)?.getTime() || 0;
      const leftTime = toDate(left.spareSwapAt)?.getTime() || 0;
      return rightTime - leftTime;
    });

  const delayDrivers = mapCounts(delayedSorties.map((row) => row.delayDriver)).slice(0, 6);
  const cancellationCauses = mapCounts(cancelledSorties.map((row) => row.cancelReasonLabel)).slice(0, 6);

  const topMaintenanceImpactTails = mapCounts([
    ...delayedSorties.map((row) => row.tail),
    ...cancelledSorties.map((row) => row.tail),
    ...issuesOpenedInRange.map((issue) => issue.tail),
  ], 'tail', 'count').slice(0, 6);

  const topDelayShops = mapCounts(
    delayedSorties.flatMap((row) => row.linkedIssues.map((issue) => issue.shopName || 'Unknown')),
    'shop',
    'count'
  ).slice(0, 6);

  const repeatTailRows7 = mapCounts(
    issuesData
      .filter((issue) => {
        const openedAt = getIssueOpenedAt(issue);
        return openedAt ? isWithinInterval(openedAt, { start: last7Start, end: last7End }) : false;
      })
      .map((issue) => issue.tail),
    'tail',
    'issues7'
  ).filter((row) => row.issues7 >= 2);

  const repeatTailRows30 = mapCounts(
    issuesData
      .filter((issue) => {
        const openedAt = getIssueOpenedAt(issue);
        return openedAt ? isWithinInterval(openedAt, { start: last30Start, end: todayEnd }) : false;
      })
      .map((issue) => issue.tail),
    'tail',
    'issues30'
  ).filter((row) => row.issues30 >= 2);

  const repeatTailDrilldown = repeatTailRows30
    .map((row) => {
      const matchingIssues = (issuesByTail[row.tail] || []);
      const openCount = matchingIssues.filter((issue) => !issue.fixed).length;
      const topShop = mapCounts(matchingIssues.map((issue) => issue.shopName || 'Unknown'), 'shop', 'count')[0]?.shop || 'Unknown';
      return {
        ...row,
        openCount,
        topShop,
      };
    })
    .sort((left, right) => right.issues30 - left.issues30)
    .slice(0, 8);

  const currentPersonnelByShop = personnelData.reduce((map, person) => {
    const shop = person['Shop Name'] || 'Unknown';
    if (!map[shop]) {
      map[shop] = { shop, assigned: 0, present: 0, unavailable: 0 };
    }
    map[shop].assigned += 1;
    if ((person['Present?'] || '').toLowerCase() === 'yes') {
      map[shop].present += 1;
    }
    return map;
  }, {});

  Object.values(currentPersonnelByShop).forEach((shop) => {
    shop.unavailable = Math.max(shop.assigned - shop.present, 0);
  });

  const personnelCoverageByShop = Object.values(currentPersonnelByShop)
    .sort((left, right) => right.assigned - left.assigned)
    .slice(0, 8);

  const allShopsForBacklog = [...new Set([
    ...Object.keys(currentPersonnelByShop),
    ...openIssuesCurrent.map((issue) => issue.shopName || 'Unknown'),
    ...issuesData.map((issue) => issue.shopName || 'Unknown'),
  ])];

  const shopBacklogRows = allShopsForBacklog
    .map((shopName) => {
      const shop = currentPersonnelByShop[shopName] || {
        shop: shopName,
        assigned: 0,
        present: 0,
        unavailable: 0,
      };
      const matchingIssues = openIssuesCurrent.filter((issue) => (issue.shopName || 'Unknown') === shopName);
      const redBalls = matchingIssues.filter((issue) => issue.redBallFlag).length;
      const nonRedBallIssues = Math.max(matchingIssues.length - redBalls, 0);
      const avgResolutionHours = calculateAverage(
        issuesData
          .filter((issue) => (issue.shopName || 'Unknown') === shopName && issue.fixed)
          .map((issue) => {
            const openedAt = getIssueOpenedAt(issue);
            const closedAt = getIssueClosedAt(issue);
            if (!openedAt || !closedAt) return null;
            return differenceInMinutes(closedAt, openedAt) / 60;
          })
          .filter((value) => value != null)
      );

      return {
        shop: shop.shop,
        openIssues: matchingIssues.length,
        redBalls,
        nonRedBallIssues,
        present: shop.present,
        assigned: shop.assigned,
        backlogPerPresent: shop.present ? roundToOne(matchingIssues.length / shop.present) : matchingIssues.length,
        avgResolutionHours,
      };
    })
    .sort((left, right) => right.openIssues - left.openIssues);

  const todaySummary = buildWindowSummary(
    atoData.filter((row) => {
      const flightDay = getFlightDay(row);
      return flightDay ? isWithinInterval(flightDay, { start: todayStart, end: todayEnd }) : false;
    }),
    issuesData.filter((issue) => {
      const openedAt = getIssueOpenedAt(issue);
      return openedAt ? isWithinInterval(openedAt, { start: todayStart, end: todayEnd }) : false;
    }),
    openIssuesCurrent
  );

  const yesterdaySummary = buildWindowSummary(
    atoData.filter((row) => {
      const flightDay = getFlightDay(row);
      return flightDay ? isWithinInterval(flightDay, { start: yesterdayStart, end: yesterdayEnd }) : false;
    }),
    issuesData.filter((issue) => {
      const openedAt = getIssueOpenedAt(issue);
      return openedAt ? isWithinInterval(openedAt, { start: yesterdayStart, end: yesterdayEnd }) : false;
    }),
    openIssuesCurrent
  );

  const last7Summary = buildWindowSummary(
    atoData.filter((row) => {
      const flightDay = getFlightDay(row);
      return flightDay ? isWithinInterval(flightDay, { start: last7Start, end: last7End }) : false;
    }),
    issuesData.filter((issue) => {
      const openedAt = getIssueOpenedAt(issue);
      return openedAt ? isWithinInterval(openedAt, { start: last7Start, end: last7End }) : false;
    }),
    openIssuesCurrent
  );

  const selectedSummary = buildWindowSummary(flightsInRange, issuesOpenedInRange, openIssuesCurrent);
  const totalDelayMinutes = delayedSorties.reduce((sum, row) => sum + row.delayMinutes, 0);
  const repeatTailCount7 = repeatTailRows7.length;
  const repeatTailCount30 = repeatTailRows30.length;
  const tailsWithOpenIssues = distinctCount(openIssuesCurrent.map((issue) => issue.tail));

  const dailySeries = eachDayOfInterval({ start: rangeStart, end: rangeEnd }).map((day) => {
    const dayKey = format(day, 'yyyy-MM-dd');
    const dayFlights = flightsByDateKey[dayKey] || [];
    const dayIssues = issuesByOpenedDateKey[dayKey] || [];
    const dayTakeoffDelays = dayFlights
      .map((row) => positiveDelayMinutes(getActualDateTime(row, 'actTakeoff'), getScheduledDateTime(row, 'schedTakeoff')))
      .filter((minutes) => minutes > 0);
    const dayLate = dayTakeoffDelays.filter(
      (minutes) => minutes < FORMAL_DELAY_THRESHOLD_MINUTES
    );
    const dayDelayed = dayTakeoffDelays.filter(
      (minutes) => minutes >= FORMAL_DELAY_THRESHOLD_MINUTES
    );

    return {
      dayKey,
      day: format(day, 'MM/dd'),
      scheduled: dayFlights.length,
      launched: dayFlights.filter(isLaunchedInProgress).length,
      completed: dayFlights.filter(isCompletedSortie).length,
      late: dayLate.length,
      delayed: dayDelayed.length,
      cancelled: dayFlights.filter((row) => row.cancelled).length,
      avgDelay: roundToOne(calculateAverage(dayDelayed)),
      issues: dayIssues.length,
      redBalls: dayIssues.filter((issue) => issue.redBallFlag).length,
    };
  });
  const hasIssueTrend = dailySeries.some((row) => row.issues > 0);
  const activeDelayTrendRows = activeDelayTrendDay && activeDelayTrendType
    ? flightsInRange
      .map((row) => ({
        ...row,
        delayMinutes: positiveDelayMinutes(
          getActualDateTime(row, 'actTakeoff'),
          getScheduledDateTime(row, 'schedTakeoff')
        ),
      }))
      .filter((row) => {
      const flightDay = getFlightDay(row);
        if (!flightDay || format(flightDay, 'yyyy-MM-dd') !== activeDelayTrendDay) {
          return false;
        }
        return activeDelayTrendType === 'late'
          ? row.delayMinutes > 0 && row.delayMinutes < FORMAL_DELAY_THRESHOLD_MINUTES
          : row.delayMinutes >= FORMAL_DELAY_THRESHOLD_MINUTES;
      })
      .sort((left, right) => right.delayMinutes - left.delayMinutes)
    : [];
  const activeDelayTrendLabel = activeDelayTrendType === 'late'
    ? 'Late sorties (1-14 min)'
    : 'Delayed sorties (15+ min)';

  const issuesByShopRange = mapCounts(
    issuesOpenedInRange.map((issue) => issue.shopName || 'Unknown'),
    'shop',
    'count'
  ).slice(0, 8);

  const milestoneDelayRows = [
    { label: 'Sched MX Show vs Actual', scheduledKey: 'schedMxShow', actualKey: 'actMxShow' },
    { label: 'Sched Crew Show vs Actual', scheduledKey: 'schedCrewShow', actualKey: 'actCrewShow' },
    { label: 'Sched Eng Start vs Actual', scheduledKey: 'schedEngStrt', actualKey: 'actEngStrt' },
    { label: 'Sched Taxi vs Actual', scheduledKey: 'schedTaxi', actualKey: 'actTaxi' },
    { label: 'Sched Takeoff vs Actual', scheduledKey: 'schedTakeoff', actualKey: 'actTakeoff' },
    { label: 'Sched Land vs Actual', scheduledKey: 'schedLand', actualKey: 'actLand' },
  ].map(({ label, scheduledKey, actualKey }) => {
    const delayValues = flightsInRange
      .map((row) => positiveDelayMinutes(getActualDateTime(row, actualKey), getScheduledDateTime(row, scheduledKey)))
      .filter((minutes) => minutes > 0);

    const trackedCount = flightsInRange.filter((row) => (
      getScheduledDateTime(row, scheduledKey) && getActualDateTime(row, actualKey)
    )).length;

    return {
      label,
      delayedCount: delayValues.length,
      trackedCount,
      avgDelayMinutes: calculateAverage(delayValues),
      maxDelayMinutes: delayValues.length ? Math.max(...delayValues) : 0,
    };
  });

  const cancellationDrilldownRows = cancelledSorties
    .filter((row) => !activeCancellationCause || row.cancelReasonLabel === activeCancellationCause)
    .slice(0, 10);

  const delayedSortieDrilldownRows = delayedSorties
    .filter((row) => !activeDelayDriver || row.delayDriver === activeDelayDriver)
    .filter((row) => !activeDelayTail || row.tail === activeDelayTail)
    .slice(0, 10);

  const repeatTailDrilldownRows = repeatTailDrilldown
    .filter((row) => !activeRepeatTail || row.tail === activeRepeatTail);

  const backlogTableRows = shopBacklogRows
    .filter((row) => !activeBacklogShop || row.shop === activeBacklogShop)
    .slice(0, 8);

  const launchRecoveryCoverage = {
    present: personnelData.filter((person) => (person['Present?'] || '').toLowerCase() === 'yes').length,
    assigned: personnelData.length,
    launchQualified: personnelData.filter((person) => (
      (person['Present?'] || '').toLowerCase() === 'yes' &&
      (person['Launch?'] || '').toLowerCase() === 'yes'
    )).length,
    recoveryQualified: personnelData.filter((person) => (
      (person['Present?'] || '').toLowerCase() === 'yes' &&
      (person['Recovery?'] || '').toLowerCase() === 'yes'
    )).length,
    leadsPresent: personnelData.filter((person) => (
      (person['Present?'] || '').toLowerCase() === 'yes' &&
      (person['Shop Lead?'] || '').toLowerCase() === 'yes'
    )).length,
    xsQualified: personnelData.filter((person) => (
      (person['Present?'] || '').toLowerCase() === 'yes' &&
      (person['Xs'] || '').toLowerCase() === 'yes'
    )).length,
  };

  const tomorrowFlights = atoData.filter((row) => row.takeoffDate === tomorrowKey);
  const tomorrowTailsWithOpenIssues = distinctCount(
    tomorrowFlights
      .filter((row) => (issuesByTail[row.tail] || []).some((issue) => !issue.fixed))
      .map((row) => row.tail)
  );
  const tomorrowMissingLaunch = tomorrowFlights.filter((row) => !(row.launchPersonnel || []).length).length;
  const tomorrowMissingRecovery = tomorrowFlights.filter((row) => row.schedLand && !row.cancelled && (row.recoveryPersonnel || []).length < 2).length;
  const tomorrowCancelled = tomorrowFlights.filter((row) => row.cancelled).length;

  const scrollToPanel = (panelRef, callback) => {
    callback?.();
    setTimeout(() => {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const repeatTailPreview = formatListPreview(
    repeatTailDrilldown.slice(0, 4).map((row) => `${row.tail} (${row.issues30})`)
  );
  const repeatTailAffected = formatListPreview(
    repeatTailDrilldown.slice(0, 4).map((row) => row.tail)
  );

  const mxShowMissDetails = mxShowMisses;
  const mxShowMissPreview = formatListPreview(
    mxShowMissDetails.slice(0, 4).map((row) =>
      `${row.tail || row.callSign || 'Unknown aircraft'} (${Math.round(row.delayMinutes)} min)`
    )
  );
  const mxShowMissShopValues = mxShowMissDetails.flatMap((row) =>
    row.linkedIssues.map((issue) => issue.shopName || 'Unknown')
  );
  const mxShowMissShops = formatListPreview(mxShowMissShopValues);
  const hasMxShowMissDrilldown = delayedSorties.some(
    (row) => row.delayDriver === 'MX Show Miss'
  );

  const tomorrowMissingLaunchTails = tomorrowFlights
    .filter((row) => !(row.launchPersonnel || []).length)
    .map((row) => row.tail || row.callSign || 'Unknown');
  const tomorrowMissingRecoveryTails = tomorrowFlights
    .filter((row) => row.schedLand && !row.cancelled && (row.recoveryPersonnel || []).length < 2)
    .map((row) => row.tail || row.callSign || 'Unknown');

  const tomorrowFlightsWithOpenIssues = tomorrowFlights
    .filter((row) => (issuesByTail[row.tail] || []).some((issue) => !issue.fixed))
    .map((row) => ({
      tail: row.tail || row.callSign || 'Unknown',
      shops: (issuesByTail[row.tail] || [])
        .filter((issue) => !issue.fixed)
        .map((issue) => issue.shopName || 'Unknown'),
    }));

  const tomorrowOpenIssueTailPreview = formatListPreview(
    tomorrowFlightsWithOpenIssues.map((row) => row.tail)
  );
  const tomorrowOpenIssueShopPreview = formatListPreview(
    tomorrowFlightsWithOpenIssues.flatMap((row) => row.shops)
  );
  const topTomorrowIssueShop = tomorrowFlightsWithOpenIssues.flatMap((row) => row.shops)[0] || shopBacklogRows[0]?.shop || '';

  const backlogPressureRows = shopBacklogRows.filter((row) => row.openIssues >= 3);
  const backlogPressurePreview = formatListPreview(
    backlogPressureRows.slice(0, 4).map((row) => `${row.shop} (${row.openIssues})`)
  );
  const topBacklogShop = backlogPressureRows[0]?.shop || shopBacklogRows[0]?.shop || '';

  const riskRows = [
    {
      tone: repeatTailCount7 > 0 ? 'high' : 'neutral',
      text: `${repeatTailCount7} tails have 2+ discrepancies in the last 7 days.`,
      why: repeatTailCount7 > 0
        ? `Multiple Issues Log entries are concentrated on ${repeatTailPreview}; this does not establish a formal repeat/recur determination.`
        : 'No tails had two or more Issues Log entries in the last 7 days.',
      affected: `Affected tails: ${repeatTailAffected}.`,
      actionLabel: repeatTailDrilldown[0]?.tail ? `Open ${repeatTailDrilldown[0].tail} issue concentration` : null,
      onAction: repeatTailDrilldown[0]?.tail
        ? () => scrollToPanel(repeatTailPanelRef, () => setActiveRepeatTail(repeatTailDrilldown[0].tail))
        : null,
    },
    {
      tone: mxShowMisses.length > 0 ? 'high' : 'neutral',
      text: `${mxShowMisses.length} sorties missed scheduled MX Show time in the selected window.`,
      why: mxShowMisses.length > 0
        ? mxShowMissShopValues.length > 0
          ? `Largest misses: ${mxShowMissPreview}. Linked maintenance shops: ${mxShowMissShops}.`
          : `Largest misses: ${mxShowMissPreview}. No linked maintenance issue or shop was recorded for these misses.`
        : 'No MX Show misses were recorded in the selected window.',
      affected: `Affected tails: ${formatListPreview(
        mxShowMissDetails.map((row) => row.tail || row.callSign || 'Unknown aircraft')
      )}.`,
      actionLabel: hasMxShowMissDrilldown ? 'Open MX Show misses' : null,
      onAction: hasMxShowMissDrilldown
        ? () => scrollToPanel(delayPanelRef, () => setActiveDelayDriver('MX Show Miss'))
        : null,
    },
    {
      tone: tomorrowMissingLaunch > 0 ? 'high' : 'neutral',
      text: `${tomorrowMissingLaunch} tomorrow sorties currently have no launch personnel assigned.`,
      why: tomorrowMissingLaunch > 0
        ? 'Those sorties do not yet have launch-line coverage assigned in the ATO.'
        : 'Tomorrow sorties currently show launch coverage assigned.',
      affected: `Affected tails: ${formatListPreview(tomorrowMissingLaunchTails)}.`,
      actionLabel: 'Open workforce snapshot',
      onAction: () => scrollToPanel(workforcePanelRef),
    },
    {
      tone: tomorrowTailsWithOpenIssues > 0 ? 'high' : 'neutral',
      text: `${tomorrowTailsWithOpenIssues} tomorrow tails already carry open maintenance issues.`,
      why: tomorrowTailsWithOpenIssues > 0
        ? `Open issues are concentrated on ${tomorrowOpenIssueTailPreview}, led by ${tomorrowOpenIssueShopPreview}.`
        : 'No tomorrow tails currently show open maintenance issues.',
      affected: `Affected tails: ${tomorrowOpenIssueTailPreview}.`,
      actionLabel: topTomorrowIssueShop ? `Open backlog for ${topTomorrowIssueShop}` : 'Open backlog panel',
      onAction: () => scrollToPanel(backlogPanelRef, () => setActiveBacklogShop(topTomorrowIssueShop || '')),
    },
    {
      tone: shopBacklogRows.filter((row) => row.openIssues >= 3).length > 0 ? 'watch' : 'neutral',
      text: `${shopBacklogRows.filter((row) => row.openIssues >= 3).length} shops are carrying backlog of 3+ open issues.`,
      why: backlogPressureRows.length > 0
        ? `Backlog pressure is concentrated in ${backlogPressurePreview}.`
        : 'No shops are currently above the backlog watch threshold.',
      affected: `Affected shops: ${formatListPreview(backlogPressureRows.map((row) => row.shop))}.`,
      actionLabel: topBacklogShop ? `Open ${topBacklogShop} backlog` : 'Open backlog panel',
      onAction: () => scrollToPanel(backlogPanelRef, () => setActiveBacklogShop(topBacklogShop || '')),
    },
  ];

  const commanderNarrative = buildNarrative(
    selectedSummary,
    delayDrivers,
    cancellationCauses,
    repeatTailDrilldown,
    riskRows
  );

  const comparisonCards = [
    { label: 'Today', summary: todaySummary },
    { label: 'Yesterday', summary: yesterdaySummary },
    { label: 'Last 7 Days', summary: last7Summary },
  ];
  const statusReportAllTails = useMemo(() => statusReport?.tails || [], [statusReport]);
  const statusReportTails = useMemo(
    () => filterStatusReportTails(statusReportAllTails, statusReportScope),
    [statusReportAllTails, statusReportScope]
  );
  const statusReportSummary = useMemo(() => summarizeStatusReportTails(statusReportTails), [statusReportTails]);
  const statusOpenXRows = useMemo(() => flattenStatusReportTailItems(statusReportTails, 'openX'), [statusReportTails]);
  const statusOverdue9188Rows = useMemo(() => flattenStatusReportTailItems(statusReportTails, 'overdue9188'), [statusReportTails]);
  const statusRed9188Rows = useMemo(() => flattenStatusReportTailItems(statusReportTails, 'red9188'), [statusReportTails]);
  const statusWatch9188Rows = useMemo(() => flattenStatusReportTailItems(statusReportTails, 'watch9188'), [statusReportTails]);
  const statusOverdue9188Groups = useMemo(() => buildStatusReportTailItemGroups(statusReportTails, 'overdue9188'), [statusReportTails]);
  const statusOverdueRed9188Groups = useMemo(() => buildStatusReportTailItemGroups(statusReportTails, ['overdue9188', 'red9188']), [statusReportTails]);
  const statusRed9188Groups = useMemo(() => buildStatusReportTailItemGroups(statusReportTails, 'red9188'), [statusReportTails]);
  const statusWatch9188Groups = useMemo(() => buildStatusReportTailItemGroups(statusReportTails, 'watch9188'), [statusReportTails]);
  const statusMissingDueStartGroups = useMemo(() => buildStatusReportTailItemGroups(statusReportTails, 'missingDueStart'), [statusReportTails]);
  const statusLinked9188Rows = useMemo(
    () => [...statusOverdue9188Rows, ...statusRed9188Rows, ...statusWatch9188Rows],
    [statusOverdue9188Rows, statusRed9188Rows, statusWatch9188Rows]
  );
  const selectedLinked9188Row = useMemo(
    () => statusLinked9188Filter
      ? statusLinked9188Rows.find(row => row.id === statusLinked9188Filter) || null
      : null,
    [statusLinked9188Filter, statusLinked9188Rows]
  );
  const filteredStatusOpenXRows = useMemo(() => {
    if (!selectedLinked9188Row) return statusOpenXRows;
    const linkedIds = new Set(selectedLinked9188Row.linkedXJobIds || []);
    return statusOpenXRows.filter(row => linkedIds.has(row.id));
  }, [selectedLinked9188Row, statusOpenXRows]);
  const statusLocationLabel = statusReport?.locations?.length
    ? statusReport.locations.join(', ')
    : selectedBase || 'Selected location';
  const statusReportScopeLabel = statusReportScope === 'hs'
    ? 'Homestation'
    : statusReportScope === 'os'
      ? 'Offstation'
      : 'Combined';
  const selectedStatusPdfSectionCount = STATUS_REPORT_PDF_SECTIONS.filter(section => statusPdfSections[section.key]).length;

  useEffect(() => {
    if (statusLinked9188Filter && !selectedLinked9188Row) {
      setStatusLinked9188Filter('');
    }
  }, [selectedLinked9188Row, statusLinked9188Filter]);

  const exportStatusReportToPDF = async () => {
    if (!statusReportRef.current) return;
    const selectedSectionKeys = new Set(
      STATUS_REPORT_PDF_SECTIONS
        .filter(section => statusPdfSections[section.key])
        .map(section => section.key)
    );
    if (!selectedSectionKeys.size) return;
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 18;
    const contentWidth = pageWidth - (margin * 2);
    const contentHeight = pageHeight - (margin * 2);
    const sections = Array.from(statusReportRef.current.querySelectorAll('[data-pdf-section]'))
      .filter(section => {
        const key = section.getAttribute('data-pdf-section');
        return selectedSectionKeys.has(key);
      });
    let y = margin;

    for (const section of sections) {
      const canvas = await html2canvas(section, {
        backgroundColor: '#eef4fa',
        scale: STATUS_PDF_CAPTURE_SCALE,
        onclone: (documentClone) => {
          documentClone.querySelectorAll('.reports-status-export-actions, .reports-status-controls, .reports-status-pdf-options').forEach(element => {
            element.style.display = 'none';
          });
          documentClone.querySelectorAll('.reports-status-page .reports-table-shell').forEach(element => {
            element.style.overflow = 'visible';
            element.style.maxWidth = 'none';
          });
          documentClone.querySelectorAll('.reports-status-page .reports-panel').forEach(element => {
            element.style.overflow = 'visible';
          });
          documentClone.querySelectorAll('.reports-status-page .reports-status-table').forEach(element => {
            element.style.width = `${element.scrollWidth}px`;
          });
        },
      });
      const imgData = canvas.toDataURL('image/jpeg', STATUS_PDF_IMAGE_QUALITY);
      const sectionHeight = (canvas.height * contentWidth) / canvas.width;
      const renderHeight = Math.min(sectionHeight, contentHeight);
      const renderWidth = sectionHeight > contentHeight
        ? (canvas.width * renderHeight) / canvas.height
        : contentWidth;

      if (y > margin && y + renderHeight > pageHeight - margin) {
        pdf.addPage();
        y = margin;
      }

      pdf.addImage(imgData, 'JPEG', margin, y, renderWidth, renderHeight, undefined, 'FAST');
      y += renderHeight + 12;
    }

    pdf.save(`g081_status_${statusLocationLabel.replace(/[^a-z0-9]+/gi, '_')}_${statusReportScope}.pdf`);
    setStatusPdfPickerOpen(false);
  };

  const exportStatusReportToPowerPoint = async () => {
    const pptx = new PptxGenJS();
    pptx.layout = 'LAYOUT_WIDE';
    pptx.author = 'MFE FAST';
    pptx.subject = 'G081 Status Report';
    pptx.title = 'G081 Status';
    pptx.company = 'Mission Focused Ethos';

    const addTitle = (slide, title, subtitle = '') => {
      slide.background = { color: 'EEF4FA' };
      slide.addShape(pptx.ShapeType.rect, {
        x: 0, y: 0, w: 13.333, h: 0.78,
        fill: { color: '173047' },
        line: { color: '173047' },
      });
      slide.addText(title, {
        x: 0.4, y: 0.18, w: 5.7, h: 0.32,
        fontFace: 'Aptos Display',
        fontSize: 18,
        bold: true,
        color: 'F4F8FB',
      });
      if (subtitle) {
        slide.addText(subtitle, {
          x: 6.0, y: 0.22, w: 6.8, h: 0.25,
          fontFace: 'Aptos',
          fontSize: 9,
          color: 'D6E4EE',
          align: 'right',
        });
      }
    };

    const addTableSlides = (title, headers, rows, emptyText) => {
      const chunks = rows.length ? rows.reduce((pages, row, index) => {
        const page = Math.floor(index / 8);
        pages[page] = pages[page] || [];
        pages[page].push(row);
        return pages;
      }, []) : [[]];
      chunks.forEach((chunk, index) => {
        const slide = pptx.addSlide();
        addTitle(slide, title, `${statusLocationLabel} | ${statusReportScopeLabel} | ${formatReportDateTime(statusReport?.generatedAt)}`);
        if (!chunk.length) {
          slide.addText(emptyText, {
            x: 0.5, y: 1.25, w: 12.1, h: 0.35,
            fontFace: 'Aptos',
            fontSize: 14,
            bold: true,
            color: '536B80',
          });
          return;
        }
        slide.addTable([headers, ...chunk], {
          x: 0.45, y: 1.05, w: 12.45, h: 5.95,
          border: { type: 'solid', color: 'C8D7E3', pt: 0.6 },
          fontFace: 'Aptos',
          fontSize: 8,
          color: '173047',
          margin: 0.06,
          fill: { color: 'FFFFFF' },
          autoFit: false,
          rowH: 0.55,
          valign: 'mid',
          breakLine: false,
          ...(index === 0 ? {} : { title: `${title} (${index + 1})` }),
        });
      });
    };

    const overviewSlide = pptx.addSlide();
    addTitle(overviewSlide, 'G081 Status', `${statusLocationLabel} | ${statusReportScopeLabel} | ${formatReportDateTime(statusReport?.generatedAt)}`);
    const cards = [
      { label: 'Tails', value: statusReportSummary.tails || 0 },
      { label: 'Open X Jobs', value: statusReportSummary.openX || 0 },
      { label: '9188 Overdue', value: statusReportSummary.overdue9188 || 0 },
      { label: '9188 Red Soon', value: statusReportSummary.red9188 || 0 },
      { label: '9188 Watch', value: statusReportSummary.watch9188 || 0 },
      { label: 'Missing Due/Start', value: statusReportSummary.missingDueStart || 0 },
      { label: '781Ks', value: statusReportSummary.form781K || 0 },
    ];
    cards.forEach((card, index) => {
      const x = 0.45 + (index % 3) * 4.15;
      const y = 1.1 + Math.floor(index / 3) * 1.2;
      overviewSlide.addShape(pptx.ShapeType.roundRect, {
        x, y, w: 3.65, h: 0.9,
        rectRadius: 0.05,
        fill: { color: 'FFFFFF' },
        line: { color: 'C8D7E3' },
      });
      overviewSlide.addText(card.label, { x: x + 0.16, y: y + 0.16, w: 2.4, h: 0.2, fontSize: 8, bold: true, color: '617C93' });
      overviewSlide.addText(String(card.value), { x: x + 0.16, y: y + 0.42, w: 1.3, h: 0.32, fontSize: 18, bold: true, color: '173047' });
    });
    addTableSlides(
      'Tail Summary',
      ['Tail', 'MDS', 'HS/OS', 'Status', 'ETIC', 'Open X', '9188 OD', '9188 Red', 'Watch', 'Drivers Missing Dates', '781K'],
      statusReportTails.map((row) => [
        row.aircraft?.tail || '--',
        row.aircraft?.mds || '--',
        row.aircraft?.hsOs || '--',
        row.aircraft?.status || '--',
        formatReportDateTime(row.aircraft?.etic),
        String(row.counts?.openX || 0),
        String(row.counts?.overdue9188 || 0),
        String(row.counts?.red9188 || 0),
        String(row.counts?.watch9188 || 0),
        String(row.counts?.missingDueStart || 0),
        String(row.counts?.form781K || 0),
      ]),
      'No G081 tails were found for this location.'
    );
    addTableSlides(
      'Open X Jobs from 8035',
      ['Tail', 'JCN', 'Shop', 'WUC', '781', 'Linked 9188', 'Discrepancy'],
      statusOpenXRows.map((row) => [
        row.tail,
        row.jcn || '--',
        row.shopName || '--',
        row.wuc || '--',
        row.form781Code || '--',
        row.linkedRequirements?.length ? row.linkedRequirements.map(item => item.noun || item.wuc || '9188-6').join(', ') : '--',
        truncateReportText(row.discrepancy, 80),
      ]),
      'No open X jobs were found.'
    );
    addTableSlides(
      '9188-6 Overdue and Red',
      ['Tail', 'Level', 'WUC', 'Requirement', 'Due', 'Remaining', 'Linked X Jobs'],
      flattenStatusReportTailItemGroups(statusOverdueRed9188Groups).map((row) => [
        row.tail,
        row.level === 'critical' ? 'Red' : 'Overdue',
        row.wuc || '--',
        truncateReportText(row.noun, 72),
        row.dueAt ? formatReportDate(row.dueAt) : (row.dueValue ?? '--'),
        row.level === 'overdue' ? `${row.overdueDays || 0} days overdue` : row.remainingLabel || '--',
        row.linkedXJobs?.length ? `Y (${row.linkedXJobs.length})` : '--',
      ]),
      'No overdue or red 9188-6 items were found.'
    );
    addTableSlides(
      '9188-6 Watch List',
      ['Tail', 'WUC', 'Requirement', 'Due', 'Remaining', 'Linked X Jobs'],
      flattenStatusReportTailItemGroups(statusWatch9188Groups).map((row) => [
        row.tail,
        row.wuc || '--',
        truncateReportText(row.noun, 72),
        row.dueAt ? formatReportDate(row.dueAt) : (row.dueValue ?? '--'),
        row.remainingLabel || '--',
        row.linkedXJobs?.length ? `Y (${row.linkedXJobs.length})` : '--',
      ]),
      'No watch-window 9188-6 items were found.'
    );
    addTableSlides(
      'Items Missing Due & Start Dates',
      ['Tail', 'Type', 'Ref', 'Shop', 'Readiness', 'Item'],
      flattenStatusReportTailItemGroups(statusMissingDueStartGroups).map((row) => [
        row.tail,
        row.itemType || row.sourceReportType || '--',
        row.itemType === '8035 Job' ? (row.jcn || row.wuc || '--') : (row.wuc || '--'),
        row.shopName || '--',
        row.affectsReadiness ? 'Yes' : 'No',
        truncateReportText(row.discrepancy || row.noun, 80),
      ]),
      'No items missing due and start dates were found.'
    );
    await pptx.writeFile({ fileName: `g081_status_${statusLocationLabel.replace(/[^a-z0-9]+/gi, '_')}_${statusReportScope}.pptx` });
  };

  const exportToPDF = async () => {
    if (!reportRef.current) return;
    const canvas = await html2canvas(reportRef.current, {
      backgroundColor: '#eef4fa',
      scale: COMMANDER_PDF_CAPTURE_SCALE,
    });
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const renderWidth = pageWidth - (margin * 2);
    const renderHeight = pageHeight - (margin * 2);
    const sourcePageHeight = Math.max(1, Math.floor((canvas.width * renderHeight) / renderWidth));
    let sourceY = 0;
    let pageNumber = 0;

    while (sourceY < canvas.height) {
      const sliceHeight = Math.min(sourcePageHeight, canvas.height - sourceY);
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHeight;
      const context = pageCanvas.getContext('2d');
      if (!context) throw new Error('Unable to create the PDF page image.');
      context.fillStyle = '#eef4fa';
      context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      context.drawImage(canvas, 0, sourceY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
      const pageImage = pageCanvas.toDataURL('image/png');
      const pageRenderHeight = (sliceHeight * renderWidth) / canvas.width;
      if (pageNumber > 0) pdf.addPage();
      pdf.addImage(pageImage, 'PNG', margin, margin, renderWidth, pageRenderHeight, undefined, 'MEDIUM');
      sourceY += sliceHeight;
      pageNumber += 1;
    }

    pdf.save(`commander_dashboard_${startDate}_to_${endDate}.pdf`);
  };

  const setQuickRange = (key) => {
    const nextRange = QUICK_RANGE_BUILDERS[key]();
    const nextStart = buildInputDateValue(nextRange.start);
    const nextEnd = buildInputDateValue(nextRange.end);
    setDraftStartDate(nextStart);
    setDraftEndDate(nextEnd);
    setStartDate(nextStart);
    setEndDate(nextEnd);
  };

  const applyDraftRange = () => {
    if (!canApplyDraftRange) {
      return;
    }
    startTransition(() => {
      setStartDate(draftStartDate);
      setEndDate(draftEndDate);
    });
  };

  const handleDateInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      applyDraftRange();
    }
  };

  const jumpToEvidence = (link) => {
    if (!link?.type) return;
    const savedRange = commanderBrief?.savedRecord;
    const evidenceStartDate = savedRange?.startDate;
    const evidenceEndDate = savedRange?.endDate;
    const shouldSyncRange = (
      isCompleteDateInput(evidenceStartDate) &&
      isCompleteDateInput(evidenceEndDate) &&
      (evidenceStartDate !== startDate || evidenceEndDate !== endDate)
    );

    if (shouldSyncRange) {
      setDraftStartDate(evidenceStartDate);
      setDraftEndDate(evidenceEndDate);
      setStartDate(evidenceStartDate);
      setEndDate(evidenceEndDate);
    }

    setActiveReportPage('main');
    setTimeout(() => {
      if (link.type === 'delayTail') {
        setActiveDelayDriver('');
        setActiveDelayTail(link.tail || '');
        delayPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (link.type === 'backlogShop') {
        setActiveBacklogShop(link.shop || '');
        backlogPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (link.type === 'multiIssueTail' || link.type === 'repeatTail') {
        setActiveRepeatTail(link.tail || '');
        repeatTailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (link.type === 'spareSwaps') {
        spareSwapPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (link.type === 'workforce') {
        workforcePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, shouldSyncRange ? 180 : 0);
  };

  const generateCommanderBrief = async () => {
    if (commanderAiBriefLocked) {
      setCommanderBriefError('Upgrade to use Commander AI Brief for this location.');
      return;
    }
    setCommanderBriefLoading(true);
    setCommanderBriefError('');
    try {
      const res = await api.post('/commander-brief', {
        email: user.email,
        location: selectedBase,
        global: user.is_superuser && globalView ? true : undefined,
        startDate,
        endDate,
      });
      const savedBrief = res.data.savedBrief;
      const nextBrief = hydrateSavedBrief(savedBrief) || res.data.brief || null;
      setCommanderBrief(nextBrief);
      if (savedBrief) {
        setCommanderBriefHistory((current) => [
          savedBrief,
          ...current.filter((record) => record.id !== savedBrief.id),
        ].slice(0, 12));
      }
    } catch (err) {
      console.error('Failed to generate commander brief', err);
      setCommanderBriefError(err.response?.data?.message || 'Unable to generate commander brief.');
    } finally {
      setCommanderBriefLoading(false);
    }
  };

  const generateSafetyBrief = async () => {
    if (commanderAiBriefLocked) {
      setSafetyBriefError('Upgrade to use Safety Intelligence for this location.');
      return;
    }
    setSafetyBriefLoading(true);
    setSafetyBriefError('');
    try {
      const res = await api.post('/commander-safety-intelligence', {
        email: user.email,
        location: selectedBase,
        global: user.is_superuser && globalView ? true : undefined,
        startDate,
        endDate,
      });
      const savedBrief = res.data.savedBrief;
      const nextBrief = hydrateSavedBrief(savedBrief) || res.data.brief || null;
      setSafetyBrief(nextBrief);
      if (savedBrief) {
        setSafetyBriefHistory((current) => [
          savedBrief,
          ...current.filter((record) => record.id !== savedBrief.id),
        ].slice(0, 12));
      }
    } catch (err) {
      console.error('Failed to generate safety intelligence', err);
      setSafetyBriefError(err.response?.data?.message || 'Unable to generate Safety Intelligence.');
    } finally {
      setSafetyBriefLoading(false);
    }
  };

  const handleCustomPresetClick = preset => {
    setCustomReportPreset(preset.key);
    setCustomReportQuestion(preset.prompt);
  };

  const toggleCustomReportDomain = domainKey => {
    setCustomReportDomains(current => {
      if (current.includes(domainKey)) {
        return current.length > 1 ? current.filter(key => key !== domainKey) : current;
      }
      return [...current, domainKey];
    });
  };

  const toggleAllCustomReportDomains = () => {
    setCustomReportDomains(current => (
      current.length === SMART_CUSTOM_REPORT_DOMAINS.length
        ? [SMART_CUSTOM_REPORT_DOMAINS[0].key]
        : SMART_CUSTOM_REPORT_DOMAINS.map(domain => domain.key)
    ));
  };

  const generateCustomReport = async () => {
    if (smartCustomReportBuilderLocked) {
      setCustomReportError('Upgrade to use Smart Custom Report Builder for this location.');
      return;
    }
    const trimmedQuestion = customReportQuestion.trim();
    if (!trimmedQuestion) {
      setCustomReportError('Enter a report question before generating.');
      return;
    }

    setCustomReportLoading(true);
    setCustomReportError('');
    try {
      const res = await api.post('/smart-custom-report', {
        email: user.email,
        location: selectedBase,
        global: user.is_superuser && globalView ? true : undefined,
        startDate,
        endDate,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        question: trimmedQuestion,
        preset: customReportPreset,
        dataDomains: customReportDomains,
      });
      const savedBrief = res.data.savedBrief;
      const nextReport = hydrateSavedBrief(savedBrief) || res.data.report || null;
      setCustomReport(nextReport);
      if (savedBrief) {
        setCustomReportHistory((current) => [
          savedBrief,
          ...current.filter((record) => record.id !== savedBrief.id),
        ].slice(0, 12));
      }
    } catch (err) {
      console.error('Failed to generate custom report', err);
      setCustomReportError(err.response?.data?.message || 'Unable to generate custom report.');
    } finally {
      setCustomReportLoading(false);
    }
  };

  const rangeControls = (
    <div className="reports-controls">
      <div className="reports-date-group">
        <label>
          <span>Start Date</span>
          <input
            type="date"
            value={draftStartDate}
            onChange={(event) => setDraftStartDate(event.target.value)}
            onBlur={applyDraftRange}
            onKeyDown={handleDateInputKeyDown}
          />
        </label>
        <label>
          <span>End Date</span>
          <input
            type="date"
            value={draftEndDate}
            onChange={(event) => setDraftEndDate(event.target.value)}
            onBlur={applyDraftRange}
            onKeyDown={handleDateInputKeyDown}
          />
        </label>
        <button
          type="button"
          className="reports-range-button reports-range-button--apply"
          disabled={!canApplyDraftRange || !hasPendingDraftRange}
          onClick={applyDraftRange}
        >
          Apply
        </button>
      </div>
      <div className="reports-quick-actions">
        {[
          ['today', 'Today'],
          ['yesterday', 'Yesterday'],
          ['last7', 'Last 7 Days'],
          ['last30', 'Last 30 Days'],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`reports-range-button ${activeRangeLabel === label ? 'is-active' : ''}`}
            onClick={() => setQuickRange(key)}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          className={`reports-range-button reports-range-button--custom ${activeRangeLabel === 'Custom' ? 'is-active' : ''}`}
          disabled
          title="Active when the selected dates do not match a preset range."
        >
          Custom Range
        </button>
      </div>
    </div>
  );

  return (
    <div className="reports-dashboard" ref={reportRef}>
      <div className="reports-header ops-page-header">
        <div className="ops-page-header__copy">
          <h2 className="ops-page-header__title">Commander Dashboard</h2>
          <p className="reports-subtitle reports-header__subtitle">
            Fleet Readiness, Near-Term Risk, and Recurring Drivers
          </p>
        </div>
        {activeReportPage !== 'status' ? (
          <button className="reports-export-button" onClick={exportToPDF}>
            Export Briefing PDF
          </button>
        ) : null}
      </div>

      <div className="reports-page-tabs">
        <button
          type="button"
          className={activeReportPage === 'main' ? 'is-active' : ''}
          onClick={() => setActiveReportPage('main')}
        >
          Main Report
        </button>
        <button
          type="button"
          className={activeReportPage === 'status' ? 'is-active' : ''}
          onClick={() => setActiveReportPage('status')}
        >
          Status
        </button>
        <button
          type="button"
          className={activeReportPage === 'commander' ? 'is-active' : ''}
          onClick={() => setActiveReportPage('commander')}
        >
          Commander AI Brief
        </button>
        <button
          type="button"
          className={activeReportPage === 'safety' ? 'is-active' : ''}
          onClick={() => setActiveReportPage('safety')}
        >
          Safety Intelligence
        </button>
        <button
          type="button"
          className={activeReportPage === 'custom' ? 'is-active' : ''}
          onClick={() => setActiveReportPage('custom')}
          title={smartCustomReportBuilderLocked ? 'Premium feature. Upgrade to unlock custom AI report building.' : ''}
        >
          Smart Custom Report Builder{smartCustomReportBuilderLocked ? ' (Locked)' : ''}
        </button>
      </div>

      {activeReportPage === 'status' && (
        <div className="reports-status-page" ref={statusReportRef}>
          <section className="reports-panel reports-status-brief" data-pdf-section="overview">
            <div className="reports-panel__header">
              <div>
                <h3>G081 Status Report</h3>
                <div className="reports-panel__hint">
                  {statusLocationLabel} | {statusReportScopeLabel} | Current as of {formatReportDateTime(statusReport?.generatedAt)}
                </div>
              </div>
              <div className="reports-status-export-actions">
                <button type="button" className="reports-export-button" onClick={() => setStatusPdfPickerOpen(current => !current)} disabled={!statusReport || statusReportLoading}>
                  Export PDF
                </button>
                {statusPdfPickerOpen ? (
                  <div className="reports-status-pdf-options reports-status-pdf-popover" role="dialog" aria-label="Status PDF table selection">
                    <span>PDF tables</span>
                    {STATUS_REPORT_PDF_SECTIONS.map(section => (
                      <label key={section.key}>
                        <input
                          type="checkbox"
                          checked={Boolean(statusPdfSections[section.key])}
                          onChange={event => setStatusPdfSections(current => ({
                            ...current,
                            [section.key]: event.target.checked,
                          }))}
                        />
                        {section.label}
                      </label>
                    ))}
                    <div className="reports-status-pdf-popover__actions">
                      <button type="button" onClick={() => setStatusPdfSections(DEFAULT_STATUS_REPORT_PDF_SECTIONS)}>All</button>
                      <button type="button" onClick={() => setStatusPdfSections(Object.fromEntries(STATUS_REPORT_PDF_SECTIONS.map(section => [section.key, false])))}>None</button>
                      <small>{selectedStatusPdfSectionCount} selected</small>
                      <button type="button" className="reports-status-pdf-popover__primary" onClick={exportStatusReportToPDF} disabled={!selectedStatusPdfSectionCount}>Generate PDF</button>
                      <button type="button" onClick={() => setStatusPdfPickerOpen(false)}>Cancel</button>
                    </div>
                  </div>
                ) : null}
                <button type="button" className="reports-export-button" onClick={exportStatusReportToPowerPoint} disabled={!statusReport || statusReportLoading}>
                  Export PowerPoint
                </button>
              </div>
            </div>
            {statusReportLoading && <p className="reports-empty">Loading current G081 status...</p>}
            {statusReportError && <p className="commander-brief-error">{statusReportError}</p>}
            {!statusReportLoading && !statusReportError && (
              <>
                <div className="reports-status-controls">
                  <StatusReportScopeToggle scope={statusReportScope} onChange={setStatusReportScope} />
                </div>
                <div className="reports-kpi-grid reports-kpi-grid--status">
                  <div className="reports-kpi-card">
                    <span className="reports-kpi-card__label">Tails in Scope</span>
                    <strong>{statusReportSummary.tails || 0}</strong>
                    <span className="reports-kpi-card__meta">{statusReportSummary.tailsWithOpenX || 0} tails with open X jobs</span>
                  </div>
                  <div className="reports-kpi-card">
                    <span className="reports-kpi-card__label">Open X Jobs</span>
                    <strong>{statusReportSummary.openX || 0}</strong>
                    <span className="reports-kpi-card__meta">8035 symbol X jobs</span>
                  </div>
                  <div className="reports-kpi-card">
                    <span className="reports-kpi-card__label">9188-6 Overdue</span>
                    <strong>{statusReportSummary.overdue9188 || 0}</strong>
                    <span className="reports-kpi-card__meta">Past due baseline tasks</span>
                  </div>
                  <div className="reports-kpi-card">
                    <span className="reports-kpi-card__label">9188-6 Red Soon</span>
                    <strong>{statusReportSummary.red9188 || 0}</strong>
                    <span className="reports-kpi-card__meta">Inside 10% trigger window</span>
                  </div>
                  <div className="reports-kpi-card">
                    <span className="reports-kpi-card__label">9188-6 Watch</span>
                    <strong>{statusReportSummary.watch9188 || 0}</strong>
                    <span className="reports-kpi-card__meta">Inside 25% watch window</span>
                  </div>
                  <div className="reports-kpi-card">
                    <span className="reports-kpi-card__label">Missing Due/Start</span>
                    <strong>{statusReportSummary.missingDueStart || 0}</strong>
                    <span className="reports-kpi-card__meta">Readiness drivers and open X jobs</span>
                  </div>
                  <div className="reports-kpi-card">
                    <span className="reports-kpi-card__label">Total 781Ks</span>
                    <strong>{statusReportSummary.form781K || 0}</strong>
                    <span className="reports-kpi-card__meta">Open delayed discrepancies</span>
                  </div>
                </div>
              </>
            )}
          </section>

          {!statusReportLoading && !statusReportError && (
            <>
              <section className="reports-panel" data-pdf-section="tail-summary">
                <div className="reports-panel__header">
                  <div>
                    <h3>Tail Summary</h3>
                    <div className="reports-panel__hint">Counts are grouped by tail number for the selected location.</div>
                  </div>
                </div>
                <div className="reports-table-shell">
                  <table className="reports-table reports-status-table">
                    <thead>
                      <tr>
                        <th>Tail #</th>
                        <th>MDS</th>
                        <th>HS/OS</th>
                        <th>Status</th>
                        <th>ETIC</th>
                        <th>Open X</th>
                        <th>9188 Overdue</th>
                        <th>9188 Red</th>
                        <th>Watch</th>
                        <th title="Readiness drivers and open X jobs missing due/start information">Drivers Missing Dates</th>
                        <th>781Ks</th>
                        <th>Top Drivers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusReportTails.length === 0 ? (
                        <tr><td colSpan="12">No G081 tails found for this location.</td></tr>
                      ) : statusReportTails.map((row) => {
                        const drivers = [
                          ...(row.openX || []).slice(0, 1).map(item => `X: ${item.discrepancy || item.jcn || ''}`.trim()),
                          ...(row.missingDueStart || []).slice(0, 1).map(item => `Missing dates: ${item.discrepancy || item.noun || item.jcn || item.wuc || ''}`.trim()),
                          ...(row.overdue9188 || []).slice(0, 1).map(item => `Overdue ${item.noun || item.wuc || ''}`.trim()),
                          ...(row.red9188 || []).slice(0, 1).map(item => `Red ${item.noun || item.wuc || ''}`.trim()),
                          ...(row.watch9188 || []).slice(0, 1).map(item => `Watch ${item.noun || item.wuc || ''}`.trim()),
                        ];
                        return (
                          <tr key={row.aircraft?.id || row.aircraft?.tail}>
                            <td><strong>{row.aircraft?.tail || '--'}</strong></td>
                            <td>{row.aircraft?.mds || '--'}</td>
                            <td>{row.aircraft?.hsOs || '--'}</td>
                            <td><AircraftStatusPill status={row.aircraft?.status} /></td>
                            <td>{formatReportDateTime(row.aircraft?.etic)}</td>
                            <td>{row.counts?.openX || 0}</td>
                            <td>{row.counts?.overdue9188 || 0}</td>
                            <td>{row.counts?.red9188 || 0}</td>
                            <td>{row.counts?.watch9188 || 0}</td>
                            <td>{row.counts?.missingDueStart || 0}</td>
                            <td>{row.counts?.form781K || 0}</td>
                            <td>{drivers.length ? truncateReportText(drivers.join(' | '), 140) : '--'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              <div className="reports-grid reports-grid--two">
                <section className="reports-panel" data-pdf-section="open-x">
                  <div className="reports-panel__header">
                    <div>
                      <h3>Open X from 8035</h3>
                      <div className="reports-panel__hint">
                        {selectedLinked9188Row
                          ? `Filtered to X jobs linked to ${selectedLinked9188Row.tail} / ${selectedLinked9188Row.noun || selectedLinked9188Row.wuc || '9188-6 item'}.`
                          : 'Executable 8035 jobs currently carrying symbol X.'}
                      </div>
                    </div>
                    {selectedLinked9188Row ? (
                      <button type="button" className="reports-status-link-clear" onClick={() => setStatusLinked9188Filter('')}>
                        Clear linked filter
                      </button>
                    ) : null}
                  </div>
                  <div className="reports-table-shell">
                    <table className="reports-table reports-status-table">
                      <thead>
                        <tr><th>Tail</th><th>JCN</th><th>Shop</th><th>WUC</th><th>781</th><th>Linked 9188</th><th>Discrepancy</th></tr>
                      </thead>
                      <tbody>
                        {filteredStatusOpenXRows.length === 0 ? (
                          <tr><td colSpan="7">{selectedLinked9188Row ? 'No linked open X jobs found for the selected 9188-6 item.' : 'No open X jobs found.'}</td></tr>
                        ) : filteredStatusOpenXRows.map((row) => (
                          <tr key={row.id}>
                            <td>{row.tail}</td>
                            <td>{row.jcn || '--'}</td>
                            <td>{row.shopName || '--'}</td>
                            <td>{row.wuc || '--'}</td>
                            <td>{row.form781Code || '--'}</td>
                            <td>{row.linkedRequirements?.length ? row.linkedRequirements.map(item => item.noun || item.wuc || '9188-6').join(', ') : '--'}</td>
                            <td>{truncateReportText(row.discrepancy, 120)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="reports-panel" data-pdf-section="overdue-9188">
                  <div className="reports-panel__header">
                    <div>
                      <h3>9188-6 Overdue</h3>
                      <div className="reports-panel__hint">Recurring requirements already past due.</div>
                    </div>
                  </div>
                  <div className="reports-table-shell">
                    <table className="reports-table reports-status-table">
                      <thead>
                        <tr><th>Tail</th><th>WUC</th><th>Requirement</th><th>Due</th><th>Overdue</th><th>8035 linked X jobs</th></tr>
                      </thead>
                      <tbody>
                        {statusOverdue9188Groups.length === 0 ? (
                          <tr><td colSpan="6">No overdue 9188-6 items found.</td></tr>
                        ) : statusOverdue9188Groups.map((group) => (
                          <React.Fragment key={`overdue-group-${group.id}`}>
                            <StatusReportTailGroupHeader group={group} colSpan={6} label="overdue 9188-6 item" />
                            {group.items.map((row) => {
                              const linkedXCount = row.linkedXJobs?.length || 0;
                              return (
                                <tr
                                  key={row.id}
                                  className={[
                                    'reports-status-tail-item-row',
                                    linkedXCount ? 'reports-status-link-row' : '',
                                    statusLinked9188Filter === row.id ? 'is-selected-link' : '',
                                  ].filter(Boolean).join(' ')}
                                  onClick={linkedXCount ? () => setStatusLinked9188Filter(current => current === row.id ? '' : row.id) : undefined}
                                >
                                  <td><span className="reports-status-tail-item-marker" aria-hidden="true"></span></td>
                                  <td>{row.wuc || '--'}</td>
                                  <td>{truncateReportText(row.noun, 96)}</td>
                                  <td>{row.dueAt ? formatReportDate(row.dueAt) : (row.dueValue ?? '--')}</td>
                                  <td><span className="reports-status-pill reports-status-pill--high">{row.overdueDays || 0} days</span></td>
                                  <td>
                                    {linkedXCount ? (
                                      <button type="button" className="reports-status-link-pill" onClick={(event) => { event.stopPropagation(); setStatusLinked9188Filter(current => current === row.id ? '' : row.id); }}>
                                        Y{linkedXCount > 1 ? ` (${linkedXCount})` : ''}
                                      </button>
                                    ) : '--'}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>

              <div className="reports-grid reports-grid--two">
                <section className="reports-panel" data-pdf-section="red-9188">
                  <div className="reports-panel__header">
                    <div>
                      <h3>9188-6 Red Coming Due</h3>
                      <div className="reports-panel__hint">Inside the 10% trigger window based on increment and due basis.</div>
                    </div>
                  </div>
                  <div className="reports-table-shell">
                    <table className="reports-table reports-status-table">
                      <thead>
                        <tr><th>Tail</th><th>WUC</th><th>Requirement</th><th>Due</th><th>Remaining</th><th>8035 linked X jobs</th></tr>
                      </thead>
                      <tbody>
                        {statusRed9188Groups.length === 0 ? (
                          <tr><td colSpan="6">No red 9188-6 items found.</td></tr>
                        ) : statusRed9188Groups.map((group) => (
                          <React.Fragment key={`red-group-${group.id}`}>
                            <StatusReportTailGroupHeader group={group} colSpan={6} label="red 9188-6 item" />
                            {group.items.map((row) => {
                              const linkedXCount = row.linkedXJobs?.length || 0;
                              return (
                                <tr
                                  key={row.id}
                                  className={[
                                    'reports-status-tail-item-row',
                                    linkedXCount ? 'reports-status-link-row' : '',
                                    statusLinked9188Filter === row.id ? 'is-selected-link' : '',
                                  ].filter(Boolean).join(' ')}
                                  onClick={linkedXCount ? () => setStatusLinked9188Filter(current => current === row.id ? '' : row.id) : undefined}
                                >
                                  <td><span className="reports-status-tail-item-marker" aria-hidden="true"></span></td>
                                  <td>{row.wuc || '--'}</td>
                                  <td>{truncateReportText(row.noun, 96)}</td>
                                  <td>{row.dueAt ? formatReportDate(row.dueAt) : (row.dueValue ?? '--')}</td>
                                  <td><span className="reports-status-pill reports-status-pill--high">{row.remainingLabel || '--'}</span></td>
                                  <td>
                                    {linkedXCount ? (
                                      <button type="button" className="reports-status-link-pill" onClick={(event) => { event.stopPropagation(); setStatusLinked9188Filter(current => current === row.id ? '' : row.id); }}>
                                        Y{linkedXCount > 1 ? ` (${linkedXCount})` : ''}
                                      </button>
                                    ) : '--'}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="reports-panel" data-pdf-section="watch-9188">
                  <div className="reports-panel__header">
                    <div>
                      <h3>9188-6 Watch</h3>
                      <div className="reports-panel__hint">Inside the 25% watch window for planning awareness.</div>
                    </div>
                  </div>
                  <div className="reports-table-shell">
                    <table className="reports-table reports-status-table">
                      <thead>
                        <tr><th>Tail</th><th>WUC</th><th>Requirement</th><th>Due</th><th>Remaining</th><th>8035 linked X jobs</th></tr>
                      </thead>
                      <tbody>
                        {statusWatch9188Groups.length === 0 ? (
                          <tr><td colSpan="6">No 9188-6 watch items found.</td></tr>
                        ) : statusWatch9188Groups.map((group) => (
                          <React.Fragment key={`watch-group-${group.id}`}>
                            <StatusReportTailGroupHeader group={group} colSpan={6} label="watch 9188-6 item" />
                            {group.items.map((row) => {
                              const linkedXCount = row.linkedXJobs?.length || 0;
                              return (
                                <tr
                                  key={row.id}
                                  className={[
                                    'reports-status-tail-item-row',
                                    linkedXCount ? 'reports-status-link-row' : '',
                                    statusLinked9188Filter === row.id ? 'is-selected-link' : '',
                                  ].filter(Boolean).join(' ')}
                                  onClick={linkedXCount ? () => setStatusLinked9188Filter(current => current === row.id ? '' : row.id) : undefined}
                                >
                                  <td><span className="reports-status-tail-item-marker" aria-hidden="true"></span></td>
                                  <td>{row.wuc || '--'}</td>
                                  <td>{truncateReportText(row.noun, 96)}</td>
                                  <td>{row.dueAt ? formatReportDate(row.dueAt) : (row.dueValue ?? '--')}</td>
                                  <td><span className="reports-status-pill reports-status-pill--watch">{row.remainingLabel || '--'}</span></td>
                                  <td>
                                    {linkedXCount ? (
                                      <button type="button" className="reports-status-link-pill" onClick={(event) => { event.stopPropagation(); setStatusLinked9188Filter(current => current === row.id ? '' : row.id); }}>
                                        Y{linkedXCount > 1 ? ` (${linkedXCount})` : ''}
                                      </button>
                                    ) : '--'}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>

              <section className="reports-panel" data-pdf-section="missing-dates">
                <div className="reports-panel__header">
                  <div>
                    <h3>Items Missing Due & Start Dates</h3>
                    <div className="reports-panel__hint">Readiness-driving scheduled tasks with no due basis or planned start, plus readiness-driving 8035 jobs and open X jobs with no planned start.</div>
                  </div>
                </div>
                <div className="reports-table-shell">
                  <table className="reports-table reports-status-table">
                    <thead>
                      <tr><th>Tail</th><th>Type</th><th>Ref</th><th>Shop</th><th>Readiness Driver</th><th>Due</th><th>Planned Start</th><th>Item</th></tr>
                    </thead>
                    <tbody>
                      {statusMissingDueStartGroups.length === 0 ? (
                        <tr><td colSpan="8">No items missing due and start dates found.</td></tr>
                      ) : statusMissingDueStartGroups.map((group) => (
                        <React.Fragment key={`missing-dates-group-${group.id}`}>
                          <StatusReportTailGroupHeader group={group} colSpan={8} label="missing-date item" />
                          {group.items.map((row) => (
                            <tr key={`${row.itemType || row.sourceReportType || 'item'}-${row.id}`} className="reports-status-tail-item-row">
                              <td><span className="reports-status-tail-item-marker" aria-hidden="true"></span></td>
                              <td>{row.itemType || row.sourceReportType || '--'}</td>
                              <td>{row.itemType === '8035 Job' ? (row.jcn || row.wuc || '--') : (row.wuc || '--')}</td>
                              <td>{row.shopName || '--'}</td>
                              <td>{row.affectsReadiness ? 'Yes' : 'No'}</td>
                              <td><span className="reports-status-pill reports-status-pill--high">Missing</span></td>
                              <td><span className="reports-status-pill reports-status-pill--high">Missing</span></td>
                              <td>{truncateReportText(row.discrepancy || row.noun, 140)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      )}

      {activeReportPage === 'main' && (
      <>
      {rangeControls}

      <div className="reports-kpi-grid">
        <div className="reports-kpi-card">
          <span className="reports-kpi-card__label">Sorties Scheduled</span>
          <strong>{selectedSummary.scheduled}</strong>
          <span className="reports-kpi-card__meta">
            {selectedSummary.launched} launched / {selectedSummary.completed} completed
          </span>
        </div>
        <div className="reports-kpi-card">
          <span className="reports-kpi-card__label">Delayed Sorties</span>
          <strong>{selectedSummary.delayed}</strong>
          <span className="reports-kpi-card__meta">
            {selectedSummary.late} late (1-14 min) / Avg delayed {formatMinutes(selectedSummary.avgDelayMinutes)}
          </span>
        </div>
        <div className="reports-kpi-card">
          <span className="reports-kpi-card__label">Cancelled Sorties</span>
          <strong>{selectedSummary.cancelled}</strong>
          <span className="reports-kpi-card__meta">
            Delay hours lost: {formatHours(minutesToHours(totalDelayMinutes))}
          </span>
        </div>
        <div className="reports-kpi-card">
          <span className="reports-kpi-card__label">Open Maintenance Issues</span>
          <strong>{openIssuesCurrent.length}</strong>
          <span className="reports-kpi-card__meta">
            {openRedBallsCurrent.length} red-ball level
          </span>
        </div>
        <div className="reports-kpi-card">
          <span className="reports-kpi-card__label">Tails With Multiple Issues</span>
          <strong>{repeatTailCount7}</strong>
          <span className="reports-kpi-card__meta">
            {repeatTailCount30} tails had 2+ entries in the last 30 days
          </span>
        </div>
        <div className="reports-kpi-card">
          <span className="reports-kpi-card__label">Personnel Coverage</span>
          <strong>{launchRecoveryCoverage.present}/{launchRecoveryCoverage.assigned}</strong>
          <span className="reports-kpi-card__meta">
            Present vs assigned right now
          </span>
        </div>
      </div>

      <div className="reports-comparison-strip">
        {comparisonCards.map((card) => (
          <div key={card.label} className="reports-comparison-card">
            <span className="reports-comparison-card__label">{card.label}</span>
            <div className="reports-comparison-card__stats">
              <span>{card.summary.scheduled} scheduled</span>
              <span>{card.summary.launched} launched</span>
              <span>{card.summary.completed} completed</span>
              <span>{card.summary.late} late</span>
              <span>{card.summary.delayed} delayed (15+ min)</span>
              <span>{card.summary.cancelled} cancelled</span>
            </div>
          </div>
        ))}
      </div>

      {spareSwapRows.length > 0 && (
        <section className="reports-panel" ref={spareSwapPanelRef}>
          <div className="reports-panel__header">
            <h3>Spare Aircraft Swaps</h3>
            <span>{spareSwapRows.length} substitution{spareSwapRows.length === 1 ? '' : 's'} in selected window</span>
          </div>
          <div className="reports-table-shell">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Take-off Date</th>
                  <th>Line</th>
                  <th>Original Tail</th>
                  <th>Spare Tail</th>
                  <th>Reason</th>
                  <th>Swapped By</th>
                </tr>
              </thead>
              <tbody>
                {spareSwapRows.slice(0, 8).map(row => (
                  <tr key={`${row.id}-spare-swap`}>
                    <td>{row.takeoffDate || '--'}</td>
                    <td>{row.lineNum || '--'}</td>
                    <td>{row.originalTail || '--'}</td>
                    <td>{row.tail || '--'}</td>
                    <td>{row.spareSwapReason || '--'}</td>
                    <td>{row.spareSwapBy || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="reports-hero">
        <section className="reports-panel">
          <div className="reports-panel__header">
            <h3>Leadership Attention</h3>
            <span>Risk, why, affected, and next panel</span>
          </div>
          <ul className="reports-attention-list">
            {riskRows.map((risk) => (
              <li key={`${risk.text}-hero`} className={`reports-attention-card is-${risk.tone}`}>
                <div className="reports-attention-card__title">{risk.text}</div>
                <div className="reports-attention-card__row">
                  <label>Why</label>
                  <span>{risk.why}</span>
                </div>
                <div className="reports-attention-card__row">
                  <label>Affected</label>
                  <span>{risk.affected}</span>
                </div>
                <div className="reports-attention-card__footer">
                  <span className={`reports-attention-card__tone is-${risk.tone}`}>
                    {risk.tone === 'high' ? 'High' : risk.tone === 'watch' ? 'Watch' : 'Stable'}
                  </span>
                  {risk.actionLabel && (
                    <button type="button" onClick={risk.onAction}>
                      {risk.actionLabel}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>

        <div className="reports-hero__side">
          <section className="reports-panel reports-panel--compact">
            <div className="reports-panel__header">
              <h3>Selected Timeframe Summary</h3>
              <span>{activeRangeLabel}</span>
            </div>
            <div className="reports-narrative reports-narrative--compact">
              <strong>Summary of Selected Timeframe:</strong> {commanderNarrative}
            </div>
          </section>

          <section className="reports-panel">
            <div className="reports-panel__header">
              <h3>Daily Ops Snapshot</h3>
              <span>{activeRangeLabel}</span>
            </div>
            <div className="reports-stat-grid">
              <div><label>Launch Success Rate</label><strong>{formatPercent(selectedSummary.launchSuccessRate)}</strong></div>
              <div><label>On-Time Takeoff Rate</label><strong>{formatPercent(selectedSummary.onTimeTakeoffRate)}</strong></div>
              <div><label>Open Issue Tails</label><strong>{tailsWithOpenIssues}</strong></div>
              <div><label>Issues Closed</label><strong>{issuesClosedInRange.length}</strong></div>
              <div><label>Issues Opened</label><strong>{issuesOpenedInRange.length}</strong></div>
              <div><label>Red Balls Opened</label><strong>{issuesOpenedInRange.filter((issue) => issue.redBallFlag).length}</strong></div>
            </div>
          </section>

          <section className="reports-panel">
            <div className="reports-panel__header">
              <h3>Tomorrow&apos;s Impact Drivers</h3>
              <span>{format(addDays(today, 1), 'MMM d')}</span>
            </div>
            <ul className="reports-risk-list">
              <li><strong>{tomorrowFlights.length}</strong><span>sorties are currently scheduled tomorrow</span></li>
              <li><strong>{tomorrowMissingLaunch}</strong><span>tomorrow sorties are missing launch coverage</span></li>
              {recoverySchedulingEnabled ? (
                <li><strong>{tomorrowMissingRecovery}</strong><span>tomorrow sorties are missing full recovery coverage</span></li>
              ) : null}
              <li><strong>{tomorrowTailsWithOpenIssues}</strong><span>tomorrow tails already have open issues on the books</span></li>
              <li><strong>{tomorrowCancelled}</strong><span>tomorrow sorties are already marked cancelled</span></li>
              <li><strong>{mxShowMisses.length}</strong><span>MX Show misses occurred in the current window</span></li>
            </ul>
          </section>
        </div>
      </div>

      <div className="reports-grid reports-grid--two">
        <section className="reports-panel">
          <div className="reports-panel__header">
            <h3>Sortie Outcomes Trend</h3>
            <span>Scheduled vs launched vs completed</span>
          </div>
          <div className="reports-chart">
            <ResponsiveContainer width="100%" height={290}>
              <BarChart data={dailySeries}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="scheduled" fill={STATUS_COLORS.scheduled} />
                <Bar dataKey="launched" fill={STATUS_COLORS.launched} />
                <Bar dataKey="completed" fill={STATUS_COLORS.completed} />
                <Bar dataKey="cancelled" fill={STATUS_COLORS.cancelled} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="reports-panel">
          <div className="reports-panel__header">
            <div>
              <h3>Late, Delayed, And Issue Trend</h3>
              <div className="reports-panel__hint">Click a Late or Delayed point to view the affected tail or tails</div>
            </div>
            <span className="reports-delay-criteria">Late: 1-14 min | Delayed: 15+ min after scheduled takeoff</span>
          </div>
          <div className="reports-chart">
            <ResponsiveContainer width="100%" height={290}>
              <LineChart data={dailySeries}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="late"
                  name="Late (1-14 min)"
                  stroke={STATUS_COLORS.late}
                  strokeWidth={2.5}
                  dot={{ r: 3, cursor: 'pointer' }}
                  activeDot={{ r: 7, cursor: 'pointer' }}
                  onClick={({ payload }) => {
                    if ((payload?.late || 0) > 0) {
                      const isSameSelection =
                        activeDelayTrendDay === payload.dayKey && activeDelayTrendType === 'late';
                      setActiveDelayTrendDay(isSameSelection ? '' : payload.dayKey);
                      setActiveDelayTrendType(isSameSelection ? '' : 'late');
                    }
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="delayed"
                  name="Delayed (15+ min)"
                  stroke={STATUS_COLORS.delayed}
                  strokeWidth={2.5}
                  dot={{ r: 3, cursor: 'pointer' }}
                  activeDot={{ r: 7, cursor: 'pointer' }}
                  onClick={({ payload }) => {
                    if ((payload?.delayed || 0) > 0) {
                      const isSameSelection =
                        activeDelayTrendDay === payload.dayKey && activeDelayTrendType === 'delayed';
                      setActiveDelayTrendDay(isSameSelection ? '' : payload.dayKey);
                      setActiveDelayTrendType(isSameSelection ? '' : 'delayed');
                    }
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="avgDelay"
                  name="Avg Delayed Minutes"
                  stroke="#b45309"
                  strokeWidth={2.5}
                  strokeDasharray="6 4"
                />
                {hasIssueTrend && (
                  <Line
                    type="monotone"
                    dataKey="issues"
                    name="Issues Opened"
                    stroke={STATUS_COLORS.issues}
                    strokeWidth={2}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {activeDelayTrendDay && activeDelayTrendType && (
            <div className="reports-delay-inline">
              <div className="reports-active-filter">
                <span>
                  {activeDelayTrendLabel} for{' '}
                  <strong>{format(parseISO(activeDelayTrendDay), 'MM/dd/yyyy')}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setActiveDelayTrendDay('');
                    setActiveDelayTrendType('');
                  }}
                >
                  Clear
                </button>
              </div>
              <div className="reports-table-shell">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Tail</th>
                      <th>Call Sign</th>
                      <th>Sched T/O</th>
                      <th>Actual T/O</th>
                      <th>Delay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeDelayTrendRows.map((row) => (
                      <tr key={`${row.id}-trend-delay`}>
                        <td>{row.tail || '--'}</td>
                        <td>{row.callSign || '--'}</td>
                        <td>{formatScheduledMilestoneDisplay(row, 'schedTakeoff')}</td>
                        <td>{row.actTakeoff ? format(toDate(row.actTakeoff), 'HH:mm') : '--'}</td>
                        <td>{formatMinutes(row.delayMinutes)}</td>
                      </tr>
                    ))}
                    {activeDelayTrendRows.length === 0 && (
                      <tr>
                        <td colSpan={5}>No {activeDelayTrendLabel.toLowerCase()} were found for this day.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>

      <div className="reports-grid reports-grid--two">
        <section className="reports-panel" ref={delayPanelRef}>
          <div className="reports-panel__header">
            <div>
              <h3 className="reports-title-with-help">
                <span>Primary Delay Drivers</span>
                <span
                  className="reports-title-help"
                  title="Counts formally delayed sorties by one assigned driver. A sortie is delayed when actual takeoff is 15 or more minutes after scheduled takeoff. Driver priority is: Awaiting Parts, Maintenance Issue, MX Show Miss, Crew / Ops Delay, then Other / Unknown. Launch coverage gaps are grouped into MX Show Miss."
                  aria-label="Primary Delay Drivers counts sorties with actual takeoff at least 15 minutes after scheduled takeoff."
                >
                  ?
                </span>
              </h3>
              <div className="reports-panel__hint">Click a bar to filter the delayed-sorties table</div>
            </div>
            <span className="reports-drilldown-badge">Interactive drilldown</span>
          </div>
          {delayDrivers.length > 0 ? (
            <div className="reports-chart">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={delayDrivers}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" angle={-18} textAnchor="end" height={70} interval={0} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    fill={STATUS_COLORS.delayed}
                    onClick={({ activeLabel, label }) => {
                      const nextValue = activeLabel || label || '';
                      setActiveDelayTail('');
                      setActiveDelayDriver((current) => current === nextValue ? '' : nextValue);
                    }}
                    cursor="pointer"
                  >
                    {delayDrivers.map((row) => (
                      <Cell
                        key={row.label}
                        fill={activeDelayDriver && activeDelayDriver !== row.label ? '#d8e1ea' : STATUS_COLORS.delayed}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="reports-empty">No delayed sorties in the selected window.</p>
          )}
          {(activeDelayDriver || activeDelayTail) && (
            <div className="reports-active-filter">
              <span>
                Filtering delayed sorties by:{' '}
                <strong>{activeDelayDriver || 'Any driver'}</strong>
                {activeDelayTail ? <> / tail <strong>{activeDelayTail}</strong></> : null}
              </span>
              <button
                type="button"
                onClick={() => {
                  setActiveDelayDriver('');
                  setActiveDelayTail('');
                }}
              >
                Clear
              </button>
            </div>
          )}
          {(activeDelayDriver || activeDelayTail) && (
            <div className="reports-delay-inline">
              <div className="reports-table-shell">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Tail</th>
                      <th>Sched T/O</th>
                      <th>Actual T/O</th>
                      <th>Delay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {delayedSortieDrilldownRows.map((row) => (
                      <tr key={`${row.id}-delay-inline`}>
                        <td>{row.takeoffDate}</td>
                        <td>{row.tail}</td>
                        <td>{formatScheduledMilestoneDisplay(row, 'schedTakeoff')}</td>
                        <td>{row.actTakeoff ? format(toDate(row.actTakeoff), 'HH:mm') : '--'}</td>
                        <td>{formatMinutes(row.delayMinutes)}</td>
                      </tr>
                    ))}
                    {delayedSortieDrilldownRows.length === 0 && (
                      <tr>
                        <td colSpan={5}>No delayed sorties matched that driver in the selected window.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <section className="reports-panel">
          <div className="reports-panel__header">
            <div>
              <h3>Cancellation Causes</h3>
              <div className="reports-panel__hint">Click a bar to filter the cancellation detail table</div>
            </div>
            <span className="reports-drilldown-badge">Interactive drilldown</span>
          </div>
          {cancellationCauses.length > 0 ? (
            <div className="reports-chart">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={cancellationCauses}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" angle={-18} textAnchor="end" height={70} interval={0} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    fill={STATUS_COLORS.cancelled}
                    onClick={({ activeLabel, label }) => {
                      const nextValue = activeLabel || label || '';
                      setActiveCancellationCause((current) => current === nextValue ? '' : nextValue);
                    }}
                    cursor="pointer"
                  >
                    {cancellationCauses.map((row) => (
                      <Cell
                        key={row.label}
                        fill={activeCancellationCause && activeCancellationCause !== row.label ? '#d8e1ea' : STATUS_COLORS.cancelled}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="reports-empty">No cancellations in the selected window.</p>
          )}
          <div className="reports-cancellation-inline">
          {activeCancellationCause && (
            <div className="reports-active-filter">
              <span>Filtering cancellations by: <strong>{activeCancellationCause}</strong></span>
              <button type="button" onClick={() => setActiveCancellationCause('')}>Clear</button>
            </div>
          )}
          {activeCancellationCause ? (
            <div className="reports-table-shell">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Tail</th>
                    <th>Reason</th>
                    <th>Cancellation Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {cancellationDrilldownRows.map((row) => (
                    <tr key={`${row.id}-cancel-inline`}>
                      <td>{row.takeoffDate}</td>
                      <td>{row.tail}</td>
                      <td>{row.cancelReason || '--'}</td>
                      <td>{row.cancellationNotes || '--'}</td>
                    </tr>
                  ))}
                  {cancellationDrilldownRows.length === 0 && (
                    <tr>
                      <td colSpan={4}>No cancellations match the selected cause.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : null}
          </div>
        </section>

      </div>

      <div className="reports-grid reports-grid--two">
        <section className="reports-panel">
          <div className="reports-panel__header">
            <h3>Milestone Timing Breakdown</h3>
            <span>Scheduled versus actual checkpoint slippage</span>
          </div>
          <div className="reports-table-shell">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Checkpoint</th>
                  <th>Late Checkpoints</th>
                  <th>Tracked Flights</th>
                  <th>Avg Delay</th>
                  <th>Max Delay</th>
                </tr>
              </thead>
              <tbody>
                {milestoneDelayRows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td>{row.delayedCount}</td>
                    <td>{row.trackedCount}</td>
                    <td>{formatMinutes(row.avgDelayMinutes)}</td>
                    <td>{formatMinutes(row.maxDelayMinutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="reports-panel" ref={backlogPanelRef}>
          <div className="reports-panel__header">
            <div>
              <h3>Shop Backlog And Utilization</h3>
              <div className="reports-panel__hint">Click a stacked shop bar to filter the backlog table below</div>
            </div>
            <span className="reports-drilldown-badge">Interactive drilldown</span>
          </div>
          {shopBacklogRows.length > 0 ? (
            <div className="reports-chart">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={shopBacklogRows.slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="shop" angle={-18} textAnchor="end" height={70} interval={0} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="nonRedBallIssues"
                    stackId="backlog"
                    fill={STATUS_COLORS.backlogOpen}
                    name="Open Issues (Non-Red Ball)"
                    onClick={({ payload }) => {
                      const nextValue = payload?.shop || '';
                      setActiveBacklogShop((current) => current === nextValue ? '' : nextValue);
                    }}
                    cursor="pointer"
                  >
                    {shopBacklogRows.slice(0, 8).map((row) => (
                      <Cell
                        key={`${row.shop}-open`}
                        fill={activeBacklogShop && activeBacklogShop !== row.shop ? '#d8e1ea' : STATUS_COLORS.backlogOpen}
                      />
                    ))}
                  </Bar>
                  <Bar
                    dataKey="redBalls"
                    stackId="backlog"
                    fill={STATUS_COLORS.redBalls}
                    name="Red Balls"
                    onClick={({ payload }) => {
                      const nextValue = payload?.shop || '';
                      setActiveBacklogShop((current) => current === nextValue ? '' : nextValue);
                    }}
                    cursor="pointer"
                  >
                    {shopBacklogRows.slice(0, 8).map((row) => (
                      <Cell
                        key={`${row.shop}-red`}
                        fill={activeBacklogShop && activeBacklogShop !== row.shop ? '#d8e1ea' : STATUS_COLORS.redBalls}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="reports-empty">No current shop backlog data available.</p>
          )}
          {activeBacklogShop && (
            <div className="reports-active-filter">
              <span>Filtering backlog by: <strong>{activeBacklogShop}</strong></span>
              <button type="button" onClick={() => setActiveBacklogShop('')}>Clear</button>
            </div>
          )}
        </section>
      </div>

      <div className="reports-grid reports-grid--two">
        <div className="reports-panel-stack">
          <section className="reports-panel">
            <div className="reports-panel__header">
              <h3>Personnel Availability By Shop</h3>
              <span>Current present versus unavailable</span>
            </div>
            {personnelCoverageByShop.length > 0 ? (
              <div className="reports-chart">
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={personnelCoverageByShop}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="shop" angle={-18} textAnchor="end" height={58} interval={0} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="present" stackId="coverage" fill={STATUS_COLORS.present} name="Present" />
                    <Bar dataKey="unavailable" stackId="coverage" fill={STATUS_COLORS.unavailable} name="Unavailable" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="reports-empty">No personnel data available.</p>
            )}
          </section>

          <section className="reports-panel" ref={workforcePanelRef}>
            <div className="reports-panel__header">
              <h3>Workforce Snapshot</h3>
              <span>Current staffing signals</span>
            </div>
            <div className="reports-stat-grid">
              <div><label>Present</label><strong>{launchRecoveryCoverage.present}</strong></div>
              <div><label>Launch Qualified</label><strong>{launchRecoveryCoverage.launchQualified}</strong></div>
              <div><label>Recovery Qualified</label><strong>{launchRecoveryCoverage.recoveryQualified}</strong></div>
              <div><label>Shop Leads Present</label><strong>{launchRecoveryCoverage.leadsPresent}</strong></div>
              <div><label>XS Qualified Present</label><strong>{launchRecoveryCoverage.xsQualified}</strong></div>
              <div><label>Launch Gaps</label><strong>{launchCoverageGaps.length}</strong></div>
            </div>
            <div className="reports-mini-list">
              <div>Top shops driving delay: {topDelayShops.slice(0, 3).map((row) => `${row.shop} (${row.count})`).join(', ') || 'None'}</div>
              <div>Top tails with maintenance impact: {topMaintenanceImpactTails.slice(0, 3).map((row) => `${row.tail} (${row.count})`).join(', ') || 'None'}</div>
              <div>Parts-related impacts: {delayedSorties.filter((row) => row.delayDriver === 'Awaiting Parts').length + cancelledSorties.filter((row) => row.cancelCategory === 'Awaiting Parts').length}</div>
              <div>Personnel-related impacts: {delayedSorties.filter((row) => row.delayDriver === 'Crew / Ops Delay').length}</div>
            </div>
          </section>

          {recoverySchedulingEnabled ? (
            <>
              <section className="reports-panel">
                <div className="reports-panel__header">
                  <div>
                    <h3>Recovery Assignment Load</h3>
                    <div className="reports-panel__hint">Premium recovery analytics: workload balance, repeated use, and coverage gaps</div>
                  </div>
                  <span>{recoveryAssignments.length} total assignments</span>
                </div>
                <div className="reports-stat-grid">
                  <div><label>Recovery Windows</label><strong>{recoveryFlights.length}</strong></div>
                  <div><label>Fully Covered</label><strong>{recoveryFlights.length - recoveryCoverageGaps.length}</strong></div>
                  <div><label>Coverage Rate</label><strong>{formatPercent(recoveryCoverageRate)}</strong></div>
                  <div><label>Under 2 People</label><strong>{recoveryCoverageGaps.length}</strong></div>
                  <div><label>Personnel Used</label><strong>{distinctCount(recoveryAssignments.map((row) => row.empNum))}</strong></div>
                  <div><label>Top Recovery Shop</label><strong>{recoveryShopLoad[0]?.shop || 'None'}</strong></div>
                </div>
                <div className="reports-table-shell">
                  <table className="reports-table">
                    <thead>
                      <tr>
                        <th>Person</th>
                        <th>Shop</th>
                        <th>Assignments</th>
                        <th>Unique Tails</th>
                        <th>Completed</th>
                        <th>Upcoming / Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recoveryPersonnelLoad.length > 0 ? recoveryPersonnelLoad.map((row) => (
                        <tr key={row.empNum || row.name}>
                          <td>{row.name}</td>
                          <td>{row.shop}</td>
                          <td>{row.assignments}</td>
                          <td>{row.uniqueTails}</td>
                          <td>{row.completed}</td>
                          <td>{row.upcoming}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={6}>No recovery personnel assignments in the selected window.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="reports-panel">
                <div className="reports-panel__header">
                  <h3>Recovery Load By Shop</h3>
                  <span>Assignment concentration</span>
                </div>
                {recoveryShopLoad.length > 0 ? (
                  <div className="reports-chart">
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={recoveryShopLoad}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="shop" angle={-18} textAnchor="end" height={62} interval={0} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#0f766e" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="reports-empty">No recovery assignments available for the selected window.</p>
                )}
                <div className="reports-mini-list">
                  <div>Tomorrow recovery gaps: {tomorrowMissingRecovery} ({formatListPreview(tomorrowMissingRecoveryTails)})</div>
                  <div>Most-used recovery personnel: {recoveryPersonnelLoad.slice(0, 3).map((row) => `${row.name} (${row.assignments})`).join(', ') || 'None'}</div>
                  <div>Recovery shops used: {formatListPreview(recoveryShopLoad.map((row) => `${row.shop} (${row.count})`), 4)}</div>
                  <div>Use this view to spot recovery assignment imbalance by person/shop, repeated use of the same people, and recovery gaps by day or shift.</div>
                </div>
              </section>
            </>
          ) : (
            <section className="reports-panel reports-panel--compact">
              <div className="reports-panel__header">
                <div>
                  <h3>Recovery Analytics</h3>
                  <div className="reports-panel__hint">Premium add-on: recovery assignment imbalance, repeated use, and recovery gaps by day/shift.</div>
                </div>
                <span>Upgrade required</span>
              </div>
              <p className="reports-empty">
                Enable Recovery Personnel Scheduling for this location to unlock recovery workload and conflict analytics.
              </p>
            </section>
          )}
        </div>

        <div className="reports-panel-stack">
          <section className="reports-panel">
            <div className="reports-panel__header">
              <h3>Open Backlog Table</h3>
              <span>{activeBacklogShop ? `${activeBacklogShop}` : 'Click the backlog chart above to filter by shop'}</span>
            </div>
            <div className="reports-table-shell">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Shop</th>
                    <th>Open Issues</th>
                    <th>Red Balls</th>
                    <th>Present</th>
                    <th>Backlog / Present</th>
                    <th>Avg Close Time</th>
                  </tr>
                </thead>
                <tbody>
                  {backlogTableRows.map((row) => (
                    <tr key={`${row.shop}-backlog`}>
                      <td>{row.shop}</td>
                      <td>{row.openIssues}</td>
                      <td>{row.redBalls}</td>
                      <td>{row.present}/{row.assigned}</td>
                      <td>{row.backlogPerPresent}</td>
                      <td>{row.avgResolutionHours ? formatHours(row.avgResolutionHours) : '--'}</td>
                    </tr>
                  ))}
                  {backlogTableRows.length === 0 && (
                    <tr>
                      <td colSpan={6}>No backlog rows match the current drilldown.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="reports-panel">
            <div className="reports-panel__header">
              <h3>Issues By Shop</h3>
              <span>Selected window volume</span>
            </div>
            {issuesByShopRange.length > 0 ? (
              <div className="reports-chart">
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={issuesByShopRange}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="shop" angle={-18} textAnchor="end" height={62} interval={0} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#7c3aed" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="reports-empty">No issues opened in the selected window.</p>
            )}
          </section>

          <section className="reports-panel" ref={repeatTailPanelRef}>
            <div className="reports-panel__header">
              <div>
                <h3>Issue Concentration By Tail</h3>
                <div className="reports-panel__hint">Tails with 2+ Issues Log entries; click a bar to filter</div>
              </div>
              <span className="reports-drilldown-badge">Interactive drilldown</span>
            </div>
            {repeatTailDrilldown.length > 0 ? (
              <div className="reports-chart">
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={repeatTailDrilldown}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="tail" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="issues30"
                      fill={STATUS_COLORS.issues}
                      name="Issues (30d)"
                      onClick={({ payload }) => {
                        const nextValue = payload?.tail || '';
                        setActiveRepeatTail((current) => current === nextValue ? '' : nextValue);
                      }}
                      cursor="pointer"
                    >
                      {repeatTailDrilldown.map((row) => (
                        <Cell
                          key={row.tail}
                          fill={
                            activeRepeatTail && activeRepeatTail !== row.tail
                              ? '#d8e1ea'
                              : row.openCount > 0
                                ? '#b42318'
                                : '#64748b'
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="reports-empty">No tails had two or more Issues Log entries in the last 30 days.</p>
            )}
            {activeRepeatTail && (
              <div className="reports-active-filter">
                <span>Filtering issue concentration by tail: <strong>{activeRepeatTail}</strong></span>
                <button type="button" onClick={() => setActiveRepeatTail('')}>Clear</button>
              </div>
            )}
          </section>

          <section className="reports-panel">
            <div className="reports-panel__header">
              <h3>Tail Issue Concentration Drill-Down</h3>
              <span>{activeRepeatTail ? `${activeRepeatTail}` : 'Rolling 30-day issue concentration'}</span>
            </div>
            <div className="reports-table-shell">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Tail</th>
                    <th>Issues (30d)</th>
                    <th>Open</th>
                    <th>Top Shop</th>
                  </tr>
                </thead>
                <tbody>
                  {repeatTailDrilldownRows.map((row) => (
                    <tr key={`${row.tail}-repeat`}>
                      <td>{row.tail}</td>
                      <td>{row.issues30}</td>
                      <td>{row.openCount}</td>
                      <td>{row.topShop}</td>
                    </tr>
                  ))}
                  {repeatTailDrilldownRows.length === 0 && (
                    <tr>
                      <td colSpan={4}>No tails had two or more Issues Log entries in the last 30 days.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>

      </>
      )}

      {activeReportPage === 'commander' && (
        <>
          {rangeControls}
          <section className="reports-panel commander-brief-panel">
            <div className="reports-panel__header">
              <div>
                <h3>Commander AI Brief</h3>
                <div className="reports-panel__hint">
                  Uses the selected operational window plus a 30-day outlook of important ACFT Plans events, readiness drivers, and labor risk.
                </div>
              </div>
              <button
                type="button"
                className="reports-export-button commander-brief-generate"
                onClick={generateCommanderBrief}
                disabled={commanderBriefLoading || !canApplyDraftRange || commanderAiBriefLocked}
              >
                {commanderAiBriefLocked ? 'Upgrade to use AI' : commanderBriefLoading ? 'Generating...' : commanderBrief ? 'Regenerate Brief' : 'Generate Brief'}
              </button>
            </div>

            {commanderAiBriefLocked ? (
              <div className="commander-brief-upgrade">
                Commander AI Brief is not enabled for this location. Upgrade this location&apos;s subscription add-ons to generate AI-reasoned commander summaries.
              </div>
            ) : (
              <div className="commander-brief-notice">
                AI may infer likely causes from timing, linked issues, status ETICs, and change-log activity. Validate against production notes before command decisions, especially when the brief says a cause is inferred.
              </div>
            )}

            {commanderBriefError && (
              <div className="commander-brief-error">{commanderBriefError}</div>
            )}

            <div className="commander-brief-history">
              <div className="commander-brief-history__header">
                <strong>Brief History</strong>
                <span>
                  {commanderBriefHistoryLoading
                    ? 'Loading saved briefs...'
                    : commanderBriefHistory.length > 6
                      ? 'Showing latest 6'
                      : `${commanderBriefHistory.length} saved`}
                </span>
              </div>
              {commanderBriefHistory.length > 0 ? (
                <div className="commander-brief-history__list">
                  {commanderBriefHistory.slice(0, 6).map((record) => (
                    <button
                      key={record.id}
                      type="button"
                      className={`commander-brief-history__item ${commanderBrief?.savedBriefId === record.id ? 'is-active' : ''}`}
                      onClick={() => setCommanderBrief({
                        ...(record.brief || {}),
                        savedBriefId: record.id,
                        savedRecord: record,
                        generatedAt: record.generatedAt || record.brief?.generatedAt,
                        mode: record.brief?.mode || record.mode,
                        provider: record.brief?.provider || record.provider,
                        model: record.brief?.model || record.model,
                        aiConfigured: record.brief?.aiConfigured ?? record.aiConfigured,
                        aiError: record.brief?.aiError || record.aiError,
                      })}
                    >
                      <span>{record.generatedAt ? format(toDate(record.generatedAt), 'MMM d, HH:mm') : '--'}</span>
                      <strong>{record.startDate} to {record.endDate}</strong>
                      <em>{record.mode || 'brief'} {record.model ? `| ${record.model}` : ''}</em>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="commander-brief-history__empty">
                  No saved commander briefs for this base yet.
                </div>
              )}
            </div>

            {!commanderBrief && !commanderBriefLoading && !commanderAiBriefLocked && (
              <div className="commander-brief-empty">
                Generate a commander brief for {activeRangeDescription} to turn operational history and the 30-day readiness outlook into likely causes, near-term risk, and recommended leader actions.
              </div>
            )}

            {commanderBrief && (
              <div className="commander-brief-content">
                <div className="commander-brief-meta">
                  <span>{commanderBrief.mode === 'ai' ? 'AI-reasoned brief' : 'Rules-based fallback'}</span>
                  <span>AI configured: {commanderBrief.aiConfigured ? 'Yes' : 'No'}</span>
                  <span>Provider: {commanderBrief.provider || 'none'}</span>
                  {commanderBrief.model || commanderBrief.requestedModel ? (
                    <span>Model: {commanderBrief.model || commanderBrief.requestedModel}</span>
                  ) : null}
                  <span>Generated: {commanderBrief.generatedAt ? format(toDate(commanderBrief.generatedAt), 'MMM d, HH:mm') : '--'}</span>
                  {commanderBrief.outlook?.startDate && commanderBrief.outlook?.endDate ? (
                    <span>Readiness outlook: {commanderBrief.outlook.startDate} to {commanderBrief.outlook.endDate}</span>
                  ) : null}
                </div>
                {commanderBrief.aiError ? (
                  <div className="commander-brief-error">
                    AI provider call failed, so the app used the rules-based fallback for this brief. {commanderBrief.aiError}
                  </div>
                ) : null}

                <CommanderBriefContent commanderBrief={commanderBrief} jumpToEvidence={jumpToEvidence} />
              </div>
            )}
          </section>
        </>
      )}

      {activeReportPage === 'safety' && (
        <>
          {rangeControls}
          <section className="reports-panel commander-brief-panel">
            <div className="reports-panel__header">
              <div>
                <h3>Safety Intelligence</h3>
                <div className="reports-panel__hint">
                  Uses Lost Tool, Personnel Injury, UXO, and safety-related Change Log records for the selected window.
                </div>
              </div>
              <button
                type="button"
                className="reports-export-button commander-brief-generate"
                onClick={generateSafetyBrief}
                disabled={safetyBriefLoading || !canApplyDraftRange || commanderAiBriefLocked}
              >
                {commanderAiBriefLocked ? 'Upgrade to use AI' : safetyBriefLoading ? 'Generating...' : safetyBrief ? 'Regenerate Report' : 'Generate Report'}
              </button>
            </div>

            {commanderAiBriefLocked ? (
              <div className="commander-brief-upgrade">
                Safety Intelligence uses the premium Commander AI Brief add-on. Upgrade this location&apos;s subscription add-ons to generate AI safety trend analysis.
              </div>
            ) : (
              <div className="commander-brief-notice">
                AI reviews lost tool reports, injury reports, UXO reports, and safety-related change-log records. Validate against official safety reporting channels before command action.
              </div>
            )}

            {safetyBriefError && (
              <div className="commander-brief-error">{safetyBriefError}</div>
            )}

            <div className="commander-brief-history">
              <div className="commander-brief-history__header">
                <strong>Report History</strong>
                <span>{safetyBriefHistoryLoading ? 'Loading saved reports...' : `${safetyBriefHistory.length} saved`}</span>
              </div>
              {safetyBriefHistory.length > 0 ? (
                <div className="commander-brief-history__list">
                  {safetyBriefHistory.slice(0, 6).map((record) => (
                    <button
                      key={record.id}
                      type="button"
                      className={`commander-brief-history__item ${safetyBrief?.savedBriefId === record.id ? 'is-active' : ''}`}
                      onClick={() => setSafetyBrief(hydrateSavedBrief(record))}
                    >
                      <span>{record.generatedAt ? format(toDate(record.generatedAt), 'MMM d, HH:mm') : '--'}</span>
                      <strong>{record.startDate} to {record.endDate}</strong>
                      <em>{record.mode || 'safety'} {record.model ? `| ${record.model}` : ''}</em>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="commander-brief-history__empty">
                  No saved safety intelligence reports for this base yet.
                </div>
              )}
            </div>

            {!safetyBrief && !safetyBriefLoading && !commanderAiBriefLocked && (
              <div className="commander-brief-empty">
                Generate a safety intelligence report for {activeRangeDescription} to identify injury trends, repeated lost-tool patterns, escalation concerns, and safety data gaps.
              </div>
            )}

            {safetyBrief && (
              <div className="commander-brief-content">
                <div className="commander-brief-meta">
                  <span>AI safety intelligence</span>
                  <span>AI configured: {safetyBrief.aiConfigured ? 'Yes' : 'No'}</span>
                  <span>Provider: {safetyBrief.provider || 'none'}</span>
                  {safetyBrief.model || safetyBrief.requestedModel ? (
                    <span>Model: {safetyBrief.model || safetyBrief.requestedModel}</span>
                  ) : null}
                  <span>Generated: {safetyBrief.generatedAt ? format(toDate(safetyBrief.generatedAt), 'MMM d, HH:mm') : '--'}</span>
                </div>

                <article className="commander-brief-summary">
                  <h4>Executive Summary</h4>
                  <p>{safetyBrief.executiveSummary}</p>
                </article>

                <div className="commander-brief-grid">
                  <section>
                    <h4>Key Findings</h4>
                    <ul className="commander-brief-finding-list">
                      {(safetyBrief.keyFindings || []).map((finding, index) => (
                        <li key={`${finding.title || 'finding'}-${index}`}>
                          <span className={`commander-brief-severity is-${String(finding.severity || 'info').toLowerCase()}`}>
                            {finding.severity || 'Info'}
                          </span>
                          <strong>{finding.title}</strong>
                          <p>{finding.details}</p>
                          <small>{finding.evidence}</small>
                        </li>
                      ))}
                    </ul>
                  </section>

                  <section>
                    <h4>Trend / Cause Assessment</h4>
                    <ul className="commander-brief-bullets">
                      {(safetyBrief.causalAssessment || []).map((item, index) => (
                        <li key={`safety-cause-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>

                  <section>
                    <h4>Forward Safety Risk</h4>
                    <ul className="commander-brief-bullets">
                      {(safetyBrief.tomorrowRisk || []).map((item, index) => (
                        <li key={`safety-risk-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>

                  <section>
                    <h4>Recommended Leader Actions</h4>
                    <ul className="commander-brief-bullets commander-brief-actions">
                      {(safetyBrief.recommendedActions || []).map((item, index) => (
                        <li key={`safety-action-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                </div>

                {(safetyBrief.dataGaps || []).length > 0 && (
                  <section className="commander-brief-data-gaps">
                    <h4>Data Gaps / Confidence Notes</h4>
                    <ul>
                      {safetyBrief.dataGaps.map((item, index) => (
                        <li key={`safety-gap-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            )}
          </section>
        </>
      )}

      {activeReportPage === 'custom' && (
        <>
          {rangeControls}
          <section className="reports-panel commander-brief-panel smart-custom-report-builder">
            <div className="reports-panel__header">
              <div>
                <h3>Smart Custom Report Builder</h3>
                <div className="reports-panel__hint">
                  Premium AI workspace where commanders and leaders can ask customized questions and build views across operations, supply, personnel, safety, and change history.
                </div>
              </div>
              <button
                type="button"
                className="reports-export-button commander-brief-generate"
                onClick={generateCustomReport}
                disabled={customReportLoading || !canApplyDraftRange || smartCustomReportBuilderLocked}
              >
                {smartCustomReportBuilderLocked ? 'Upgrade to use AI' : customReportLoading ? 'Generating...' : 'Generate Custom Report'}
              </button>
            </div>
            {smartCustomReportBuilderLocked ? (
              <div className="commander-brief-upgrade">
                Smart Custom Report Builder is not enabled for this location. Upgrade this location&apos;s premium AI add-ons to let commanders build custom questions, views, and saved reports.
              </div>
            ) : (
              <div className="commander-brief-notice">
                This is not a generic chat window. It generates a saved report from a specific question, cites which data areas were used, suggests a useful view, and records AI usage separately under Smart Custom Report Builder.
              </div>
            )}

            <div className="smart-custom-report-layout">
              <div className="smart-custom-report-composer">
                <div className="smart-custom-report-presets">
                  <span>Preset questions</span>
                  <div>
                    {SMART_CUSTOM_REPORT_PRESETS.map(preset => (
                      <button
                        key={preset.key}
                        type="button"
                        className={customReportPreset === preset.key ? 'is-active' : ''}
                        onClick={() => handleCustomPresetClick(preset)}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="smart-custom-report-question">
                  <span>Custom report question</span>
                  <textarea
                    value={customReportQuestion}
                    onChange={event => {
                      setCustomReportQuestion(event.target.value);
                      setCustomReportPreset('custom');
                    }}
                    maxLength={1200}
                    rows={5}
                    placeholder="Ask a commander-style question, for example: Which tails are repeatedly driving cancellations and what should we do before tomorrow?"
                    disabled={smartCustomReportBuilderLocked}
                  />
                </label>
                <div className="smart-custom-report-domains">
                  <span>Data to include</span>
                  <label className="smart-custom-report-include-all">
                    <input
                      type="checkbox"
                      checked={allCustomReportDomainsSelected}
                      onChange={toggleAllCustomReportDomains}
                      disabled={smartCustomReportBuilderLocked}
                    />
                    <span>Include All</span>
                  </label>
                  <div>
                    {SMART_CUSTOM_REPORT_DOMAINS.map(domain => (
                      <label key={domain.key}>
                        <input
                          type="checkbox"
                          checked={customReportDomains.includes(domain.key)}
                          onChange={() => toggleCustomReportDomain(domain.key)}
                          disabled={smartCustomReportBuilderLocked}
                        />
                        <span>{domain.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {customReportError ? (
                  <div className="commander-brief-error">{customReportError}</div>
                ) : null}
              </div>

              <div className="commander-brief-history smart-custom-report-history">
                <div className="commander-brief-history__header">
                  <strong>Saved Custom Reports</strong>
                  <span>{customReportHistoryLoading ? 'Loading...' : `${customReportHistory.length} saved`}</span>
                </div>
                {customReportHistory.length > 0 ? (
                  <div className="commander-brief-history__list">
                    {customReportHistory.slice(0, 6).map((record) => (
                      <button
                        key={record.id}
                        type="button"
                        className={`commander-brief-history__item ${customReport?.savedBriefId === record.id ? 'is-active' : ''}`}
                        onClick={() => setCustomReport(hydrateSavedBrief(record))}
                      >
                        <span>{record.generatedAt ? format(toDate(record.generatedAt), 'MMM d, HH:mm') : '--'}</span>
                        <strong>{record.brief?.title || record.brief?.question || 'Custom report'}</strong>
                        <em>{record.startDate} to {record.endDate}</em>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="commander-brief-history__empty">
                    No saved custom reports for this base yet.
                  </div>
                )}
              </div>
            </div>

            {!customReport && !customReportLoading && !smartCustomReportBuilderLocked ? (
              <div className="commander-brief-empty">
                Generate a custom report for {activeRangeDescription} using a preset or your own commander question.
              </div>
            ) : null}

            {customReport ? (
              <div className="commander-brief-content smart-custom-report-output">
                <div className="commander-brief-meta">
                  <span>Smart custom report</span>
                  <span>Provider: {customReport.provider || 'none'}</span>
                  {customReport.model || customReport.requestedModel ? (
                    <span>Model: {customReport.model || customReport.requestedModel}</span>
                  ) : null}
                  <span>Generated: {customReport.generatedAt ? format(toDate(customReport.generatedAt), 'MMM d, HH:mm') : '--'}</span>
                </div>
                <article className="commander-brief-summary">
                  <h4>{customReport.title || 'Custom Report'}</h4>
                  {customReport.question ? <small>Question: {customReport.question}</small> : null}
                  <p>{customReport.answer}</p>
                </article>
                <div className="commander-brief-grid">
                  <section>
                    <h4>Key Insights</h4>
                    <ul className="commander-brief-finding-list">
                      {(customReport.keyInsights || []).map((item, index) => (
                        <li key={`custom-insight-${index}`}>
                          <span className={`commander-brief-severity is-${String(item.severity || 'info').toLowerCase()}`}>
                            {item.severity || 'Info'}
                          </span>
                          <strong>{item.insight}</strong>
                          <small>{item.evidence}</small>
                        </li>
                      ))}
                    </ul>
                  </section>
                  <section>
                    <h4>Recommended View</h4>
                    <p className="smart-custom-report-view">{customReport.recommendedView || 'No view recommendation returned.'}</p>
                  </section>
                  <section>
                    <h4>Data Used</h4>
                    <ul className="commander-brief-bullets">
                      {(customReport.dataUsed || customReport.dataDomains || []).map((item, index) => (
                        <li key={`custom-data-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                  <section>
                    <h4>Follow-Up Questions</h4>
                    <ul className="commander-brief-bullets">
                      {(customReport.followUpQuestions || []).map((item, index) => (
                        <li key={`custom-follow-up-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                </div>
                {(customReport.dataGaps || []).length > 0 ? (
                  <section className="commander-brief-data-gaps">
                    <h4>Data Gaps / Confidence Notes</h4>
                    <ul>
                      {customReport.dataGaps.map((item, index) => (
                        <li key={`custom-gap-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </section>
                ) : null}
              </div>
            ) : null}
          </section>
        </>
      )}
    </div>
  );
}
